import {NgTemplateOutlet} from '@angular/common';
import {AfterViewChecked, Component, HostListener, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import ExcelJS from 'exceljs';
import {CI_COLORS} from '../../../models/report/ci-colors';
import {
  faArrowUpFromBracket,
  faCheck,
  faChevronLeft,
  faCircleInfo,
  faCopy,
  faCube,
  faEllipsisVertical,
  faFile,
  faFilter,
  faFloppyDisk,
  faGear,
  faPenToSquare,
  faPlay,
  faPlus,
  faSparkles,
  faSpinner,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {faChatbicAi} from '../../util/icons/custom-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicTextareaComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {
  CheckMode,
  collectMissingOptionMessages,
  DEFAULT_PRUEFEBENE,
  EINGABE_PARAMETER_TYPES,
  findUnknownPromptVariables,
  KiEditorSection,
  KiRule,
  migrateOutputParameterOptions,
  needsOptions,
  normalizePruefebene,
  OptionsHolder,
  Pruefebene,
  PRUEFEBENE_DEFAULT_PARAMETERS,
  PRUEFEBENE_OPTIONS,
  Ruleset,
  SetEingabeparameter,
  SetOutputParameter,
  SetParameter,
} from '../models/rule-management.models';
import {parsePruefpromptSteps} from '../../../models/pruefprompt-steps';
import {MyCheckUsage, RulesetService} from '../../../services/ruleset.service';
import {RuleUsageWarningComponent} from '../rule-usage-warning/rule-usage-warning.component';
import {CheckRuleSet, ProjectFile} from '../../../api';
import {StateConfigService} from '../../../services/state/state-config.service';
import {OptionsPopupComponent} from '../options-popup/options-popup.component';
import {WorkflowService} from '../../../services/workflow.service';
import {RuleStepDescription} from '../../workflow-editor/workflow.models';
import {DocumentClassService} from '../../files/components/document-classes/document-class.service';
import {KiRuleImportPopupComponent} from './ki-rule-import-popup/ki-rule-import-popup.component';
import {readRuleImportRows} from './ki-rule-import-sheet';
import {ChatService} from '../../chatBIC/service/chat.service';
import {AiModelFieldComponent} from '../shared/ai-model-field/ai-model-field.component';
import {Subscription} from 'rxjs';
import {PermissionService} from '../../../services/permission/permission.service';
import {
  computeMyCheckDiff,
  KiProposalDocument,
  KiProposalRule,
  MyCheckDiff,
  MyCheckFieldChange,
  deriveChangeStatus,
  normalizeKiProposalDocument,
  ProposalChangeStatus
} from '../proposal/mycheck-diff';
import {MapEntryChange, RuleEntryDecisions, RuleMapField, SetRemovedRuleEntry} from '../proposal/map-entry-decisions';
import {
  ProposalReviewContext,
  ProposalReviewHost,
  ProposalReviewItem,
  ProposalReviewService,
  ProposalReviewSnapshot,
  revealProposalTarget
} from '../../../services/chat/proposal-review.service';
import {ProposalNavMarkerComponent, proposalNavTooltip} from '../../util/design/proposal-nav-marker/proposal-nav-marker.component';
import {ProposalStatusBadgeComponent} from '../../util/design/proposal-status-badge/proposal-status-badge.component';
import {MyCheckProposal, MyCheckProposalService} from '../../../services/mycheck-proposal.service';
import {DiscardOutcome} from '../../../guards/unsaved-changes.guard';
import {ChangeMarkerComponent} from '../../util/design/change-marker/change-marker.component';
import {ProposalRowComponent} from '../../util/design/proposal-row/proposal-row.component';
import {ProposalBannerComponent} from '../../util/design/proposal-banner/proposal-banner.component';
import {RemovedRuleCardComponent} from '../../util/design/removed-rule-card/removed-rule-card.component';
import {ProposalActionsComponent} from '../../util/design/proposal-actions/proposal-actions.component';
import {contentKey, createMemo} from '../../../memo';
import {RuleTestService} from '../../../services/rule-test/rule-test.service';
import {
  RuleTestPopupContext,
  RuleTestQuota,
  RuleTestRuleOption,
  ruleTestContextKey,
} from '../../../services/rule-test/rule-test.models';
import {ruleTestLimitTooltip} from '../../../services/rule-test/rule-test.format';
import {RuleTestPopupComponent} from '../shared/rule-test-popup/rule-test-popup.component';
import {buildDefaultReportConfigForRuleset} from '../../my-checks/my-checks-builder/my-checks-builder.model';
import {RowDecisions} from '../proposal/row-decisions';
import {ContextFilePickerComponent} from '../../util/context-file-picker/context-file-picker.component';


/** Skalare Regelfelder mit eigener ✓/✗-Entscheidung; Prüfparameter und Tags werden je Schlüssel entschieden. */
type KiRuleScalarPath = 'name' | 'description' | 'configJson.ruleShortCode' | 'configJson.allowedFileTypes'
  | 'configJson.allowedDocumentClasses';

@Component({
  selector: 'app-ki-editor',
  standalone: true,
  imports: [
    PageHeaderComponent, FaIconComponent, BicButtonComponent, BicBadgeComponent,
    BicInputComponent, BicSelectComponent, BicDropdownElementComponent, BicPopupComponent,
    ReactiveFormsModule, OptionsPopupComponent, BicTextareaComponent, TooltipDirective,
    RuleUsageWarningComponent, KiRuleImportPopupComponent, AiModelFieldComponent,
    ChangeMarkerComponent, ProposalBannerComponent, RemovedRuleCardComponent, RuleTestPopupComponent,
    NgTemplateOutlet, ChangeMarkerComponent, ProposalRowComponent, ProposalBannerComponent, RemovedRuleCardComponent,
    ProposalActionsComponent, ContextFilePickerComponent, ProposalNavMarkerComponent, ProposalStatusBadgeComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './ki-editor.component.html',
})
export class KiEditorComponent implements OnInit, OnDestroy, AfterViewChecked, ProposalReviewHost {

  /** Stabile Rueckgabewerte fuer Template-Bindungen - siehe memo.ts. */
  private readonly memo = createMemo();

  private static readonly MAX_RULE_COUNT = 30;
  private static readonly RULE_SHORT_CODE_MAX_LENGTH = 10;
  private static readonly REQUIRED_NECESSITY = 'Pflicht';
  @ViewChild('allowedDocumentClassesSelectAllOption') allowedDocumentClassesSelectAllOption?: BicDropdownElementComponent;
  @ViewChild('allowedFileTypesSelectAllOption') allowedFileTypesSelectAllOption?: BicDropdownElementComponent;
  @ViewChild('selectedRuleAllowedDocumentClassesSelectAllOption') selectedRuleAllowedDocumentClassesSelectAllOption?: BicDropdownElementComponent;
  @ViewChild('selectedRuleAllowedFileTypesSelectAllOption') selectedRuleAllowedFileTypesSelectAllOption?: BicDropdownElementComponent;
  private suppressNextAllowedDocumentClassesChange = false;
  private suppressNextAllowedFileTypesChange = false;
  private suppressNextSelectedRuleAllowedDocumentClassesChange = false;
  private suppressNextSelectedRuleAllowedFileTypesChange = false;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private stateConfigService = inject(StateConfigService);
  private rulesetService = inject(RulesetService);
  private permissionService = inject(PermissionService);
  /**
   * True, solange der Quickstart für dieses myCheck läuft.
   *
   * <p>Der Chat selbst läuft im Floating-Chat (Start auf der myChecks-Übersicht) – hier dient das
   * Flag als **Schutz**: der Agent schreibt den myCheck serverseitig, während dieses Formular noch
   * den Stand vom Öffnen hält. Ein „Übernehmen" würde seine Arbeit überschreiben.
   */
  quickstartActive = false;
  protected chatService = inject(ChatService);
  /**
   * Von chatBIC im Edit-Modus vorgeschlagene Änderung, bereits in den Formularzustand geschrieben
   * (siehe {@link applyProposal}), aber noch nicht gespeichert. `null` = kein offener Vorschlag.
   */
  pendingProposal: MyCheckProposal | null = null;
  private rulesetId = '';
  private quickstartCompletedSubscription?: Subscription;
  /** Feld- und regelgenauer Unterschied zwischen dem Stand vor dem Vorschlag und dem Vorschlag selbst. */
  proposalDiff: MyCheckDiff | null = null;
  /** Ladezustand für die Banner-Buttons (Übernehmen/Verwerfen), getrennt von `saving`. */
  applyingProposalAction = false;
  private proposalService = inject(MyCheckProposalService);
  private proposalSubscription?: Subscription;
  /** Formularzustand unmittelbar vor `applyProposal`, zum Wiederherstellen bei „Verwerfen". */
  private preProposalSnapshot: KiProposalDocument | null = null;
  /**
   * Set-Feld-Pfade, die der Nutzer einzeln über den Übernehmen-Button eines `app-change-marker`
   * bestätigt hat (siehe {@link onFieldAccepted}). Ein globales „Verwerfen" im Banner darf diese
   * Felder danach nicht mehr stillschweigend zurücksetzen – siehe {@link buildPartialRevertDocument}.
   */
  private individuallyAcceptedPaths = new Set<string>();
  /** Wie {@link individuallyAcceptedPaths}, aber je Regel (`rule.id` → bestätigte Pfade dieser Regel). */
  private individuallyAcceptedRulePaths = new Map<string, Set<string>>();
  /**
   * `rule.id`s, deren Entfernung der Nutzer auf der Karte einzeln bestätigt hat (siehe
   * {@link onRemovedRuleAccepted}). Ein globales „Verwerfen" im Banner holt diese Regeln danach nicht
   * wieder zurück – siehe {@link buildPartialRevertDocument}.
   */
  private individuallyAcceptedRemovedRuleIds = new Set<string>();
  /**
   * Entscheidung je Prüfparameter bzw. Tag einer Regel (#2789): Der Diff kennt nur den Pfad
   * `configJson.parameterValues`/`configJson.tagValues` als Ganzes, der bleibt als Sammelmarke stehen,
   * bis jeder Schlüssel darin einzeln übernommen oder verworfen ist.
   */
  private readonly ruleEntryDecisions = new RuleEntryDecisions();
  /** Prüfparameter- bzw. Tag-IDs des Sets direkt nach dem Anwenden des Vorschlags (für {@link closeOrphanedRuleEntries}). */
  private proposalSetEntryIds: Record<RuleMapField, Set<string>> = {parameterValues: new Set(), tagValues: new Set()};
  /** Kopplung an die Review-Leiste im Edit-Chat (Zähler, Stepper, „Alle übernehmen/verwerfen“). */
  protected readonly proposalReview = inject(ProposalReviewService);
  private unregisterReview?: () => void;
  protected readonly proposalNavTooltip = proposalNavTooltip;
  // ─── Systemprompt (set-level) ───────────────────────────────────────────────
  /** Leer bis app-ai-model-field das Standardmodell des Kunden meldet bzw. das Set geladen ist. */
  aiModel = '';
  private fb = new FormBuilder();

  ruleset!: Ruleset;
  rules: KiRule[] = [];
  selectedSection: KiEditorSection = 'general';
  selectedRuleKey: string | null = null;
  form: FormGroup = new FormBuilder().group({
    name: [''],
    description: [''],
    allowedDocumentClasses: [[]],
    allowedFileTypes: [[]],
    checkMode: ['SINGLE'],
  });

  // ─── Regelparameter (set-level) ─────────────────────────────────────────────
  inputParameters: { id: string; name: string; dataType: string; necessity: string }[] = [];
  checkParameters: SetParameter[] = [];
  tags: SetParameter[] = [];
  // 'Auswahl' schränkt den Wert ein, den der Nutzer je Prüfregel setzt (Optionen am Parameter) –
  // dasselbe Muster, mit dem Tags seit immer arbeiten.
  parameterDataTypes = ['Text', 'Zahl', 'Wahrheitswert', 'Leer', 'Auswahl'];
  // ─── Prüfebene + Eingabeparameter (set-level, treibt 3/4 Berichtsspalten + KI-Positionen) ──
  pruefebene: Pruefebene = DEFAULT_PRUEFEBENE;
  identifikatorParamId = '';
  eingabeparameter: SetEingabeparameter[] = [];
  /** KI-Anweisung zur Filterung der Prüfebene (z. B. "nur Kapitel zum Brandschutz"). Nur bei pruefebene ≠ gesamtdokument relevant. */
  pruefebeneFilter = '';
  readonly pruefebeneOptions = PRUEFEBENE_OPTIONS;
  readonly eingabeParameterTypes = EINGABE_PARAMETER_TYPES;
  private workflowService = inject(WorkflowService);
  availableFileTypes = ['IFC', 'PDF', 'Excel', 'Word', 'PowerPoint'];
  availableDocumentClasses: { id: string; name: string }[] = [];
  // ─── Rule context menu ────────────────────────────────────────────────────
  ruleMenuOpenKey: string | null = null;
  /** Prüfregel, für die die Löschbestätigung offen ist (null = kein Popup) */
  ruleToDelete: KiRule | null = null;
  /** myChecks, die die zu löschende Prüfregel verwenden */
  deleteUsages: MyCheckUsage[] = [];
  deleteUsagesLoading = false;
  systemprompt = '';
  pruefprompt = '';
  optimizingSystemprompt = false;
  optimizingPruefprompt = false;
  previousSystemprompt: string | null = null;
  previousPruefprompt: string | null = null;
  promptSuggestionPopupVisible = false;

  contextFiles: { id: string; label: string; name: string; type: string; size: string; date: string }[] = [];

  // ─── Ausgabeformat (set-level) ──────────────────────────────────────────────
  outputParameters: SetOutputParameter[] = [];
  modusOptions = ['KI-Generiert', 'Prüfparameter', 'Eigener Wert'];
  dateitypOptions = ['Text', 'Zahl', 'Boolean', 'Auswahl'];
  showValidation = false;
  validationErrors: string[] = [];
  private invalidRuleKeys = new Set<string>();

  // ─── Icons ──────────────────────────────────────────────────────────────────
  protected readonly faChevronLeft = faChevronLeft;
  /** True, solange das Regelset geladen wird (Ladezustand statt leerem Formular). */
  loading = false;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faGear = faGear;
  protected readonly faSparkles = faSparkles;
  protected readonly faSpinner = faSpinner;
  protected readonly faFile = faFile;
  protected readonly faCube = faCube;
  protected readonly faPlus = faPlus;
  protected readonly faCopy = faCopy;
  protected readonly faFilter = faFilter;
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faPenToSquare = faPenToSquare;
  protected readonly faTrash = faTrash;
  protected readonly faCheck = faCheck;
  protected readonly faArrowUpFromBracket = faArrowUpFromBracket;
  protected readonly faFloppyDisk = faFloppyDisk;
  protected readonly faChatbicAi = faChatbicAi;
  protected readonly faPlay = faPlay;

  // ─── Testlauf („Prüfregel testen", #2356) ───────────────────────────────────
  private ruleTestService = inject(RuleTestService);
  /** Beim Öffnen des Popups EINMAL berechnet (Feld, kein Getter – siehe Frontend/AGENTS.md). */
  ruleTestContext: RuleTestPopupContext | null = null;
  ruleTestQuota: RuleTestQuota | null = null;
  /** Ein Ergebnis liegt bereit, das noch niemand angesehen hat (Punkt am Button). */
  ruleTestUnseen = false;
  private ruleTestKey = '';
  /** Run-ID aus `?ruleTest=` – wird nach dem Laden des Sets geöffnet. */
  private pendingRuleTestRunId: string | null = null;
  private ruleTestSubscriptions = new Subscription();
  private unregisterRuleTestEditor?: () => void;

  // ─── Optionen-Popup (Tags, Ausgabe-, Prüf- und Eingabeparameter) ────────────
  optionsPopupVisible = false;
  optionsPopupLabel = '';
  optionsPopupOptions: string[] = [];
  /**
   * Was der Dialog gerade bearbeitet. Der Dialog selbst kennt nur `string[]`; die Art bestimmt,
   * in welche Liste die Optionen zurückgeschrieben werden (und welche Array-Referenz für die
   * Change Detection erneuert werden muss).
   */
  private optionsPopupKind: 'tag' | 'output' | 'check' | 'input' | null = null;
  private optionsPopupTarget: OptionsHolder | null = null;
  private documentClassService = inject(DocumentClassService);
  private readonly localRuleKeys = new WeakMap<KiRule, string>();

  // ─── Filter popup ──────────────────────────────────────────────────────────
  filterPopupVisible = false;
  activeTagFilters: string[] = [];
  /** Map of tagId -> selected option values */
  activeTagValueFilters: Map<string, Set<string>> = new Map();
  /** Selected category in filter popup ('all' or tag.id) */
  filterCategory = 'all';
  /** Search term within filter popup */
  filterSearch = '';

  // ─── Rule import popup (Excel) ───────────────────────────────────────────────
  importPopupVisible = false;
  importingRules = false;
  /** Bestehende Prüfregeln (gleiches Kürzel/Name) beim Import überschreiben statt sie zu überspringen. */
  importReplaceExisting = false;
  readonly MAX_RULES = KiEditorComponent.MAX_RULE_COUNT;

  // ─── File picker popup ──────────────────────────────────────────────────────
  filePickerVisible = false;
  private toastService = inject(BicToastService);
  protected readonly faSpinnerThird = faSpinnerThird;

  /** Dynamisch berechnete Prompt-Variablen */
  get promptVariables(): string[] {
    const variables: string[] = ['{Prüfregelname}'];
    for (const param of this.inputParameters) {
      if (param.name?.trim()) variables.push(`{${param.name}}`);
    }
    for (const param of this.checkParameters) {
      if (param.name?.trim()) variables.push(`{${param.name}}`);
    }
    for (const file of this.contextFiles) {
      if (file.label?.trim()) variables.push(`{${file.label}}`);
    }
    return variables;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   INIT
  // ═══════════════════════════════════════════════════════════════════════════

  get selectedRule(): KiRule | undefined {
    return this.rules.find(rule => this.getRuleKey(rule) === this.selectedRuleKey);
  }

  /** Filtered rules based on active tag value filters */
  get filteredRules(): KiRule[] {
    const rules = this.activeTagValueFilters.size === 0 ? this.rules : this.rules.filter(rule => {
      // Rule must match ALL tag filters (AND logic between tags)
      for (const [tagId, selectedValues] of this.activeTagValueFilters) {
        const ruleValue = rule.tagValues?.[tagId];
        // Rule must have one of the selected values for this tag (OR logic within a tag)
        if (!ruleValue || !selectedValues.has(ruleValue)) {
          return false;
        }
      }
      return true;
    });
    return [...rules].sort((a, b) => this.compareRules(a, b));
  }

  /** Reihenfolge der Regeln in der Seitenleiste (auch für die Review-Leiste, siehe {@link proposalReviewSnapshot}). */
  private compareRules(a: KiRule, b: KiRule): number {
    const aKey = this.getRuleSortKey(a);
    const bKey = this.getRuleSortKey(b);
    const byPrimary = aKey.localeCompare(bKey, 'de', {sensitivity: 'base', numeric: true});
    if (byPrimary !== 0) return byPrimary;

    const byName = (a.name ?? '').localeCompare(b.name ?? '', 'de', {sensitivity: 'base', numeric: true});
    if (byName !== 0) return byName;

    return this.getRuleKey(a).localeCompare(this.getRuleKey(b), 'de', {sensitivity: 'base', numeric: true});
  }

  /** View-only Modus (bereitgestelltes Regelwerk ohne Bearbeitungsrecht). */
  protected readOnly = false;
  /** Das Set gehört einem anderen Projekt und ist nur hierher geteilt – bearbeitet wird es dort. */
  protected linkedFromOtherProject = false;
  /** Admin-Modus: globales Regelwerk ohne Projektkontext (Speichern an globale Endpunkte). */
  protected adminGlobal = false;
  saving = false;
  private initialState = '';

  get isDirty(): boolean {
    return !this.readOnly && !!this.initialState && this.initialState !== this.serializeEditorState();
  }

  get canCreateRule(): boolean {
    return this.rules.length < this.MAX_RULES;
  }

  get canAddRule(): boolean {
    return this.canCreateRule && !this.hasUneditedEmptyRule();
  }

  get maxRulesTooltip(): string {
    if (!this.canCreateRule) {
      return `Maximal ${this.MAX_RULES} Prüfregeln möglich`;
    }
    if (this.hasUneditedEmptyRule()) {
      return 'Bitte die neue leere Prüfregel zuerst bearbeiten.';
    }
    return 'Neu hinzufügen';
  }

  ngAfterViewChecked(): void {
    this.allowedDocumentClassesSelectAllOption?.setSelected(this.areAllAllowedDocumentClassesSelected());

    const allFileTypesSelected = this.areAllValuesSelected(this.getAllowedFileTypesValue(), this.availableFileTypes);
    this.allowedFileTypesSelectAllOption?.setSelected(allFileTypesSelected);

    const selectedRuleAllowedDocumentClassIds = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    const allSelectedRuleDocumentClassesSelected = this.areAllValuesSelected(
      this.getSelectedRuleAllowedDocumentClassesSelectionValue(),
      selectedRuleAllowedDocumentClassIds,
    );
    this.selectedRuleAllowedDocumentClassesSelectAllOption?.setSelected(allSelectedRuleDocumentClassesSelected);

    const selectedRuleAllowedFileTypes = this.getRuleAllowedFileTypeOptions();
    const allSelectedRuleFileTypesSelected = this.areAllValuesSelected(
      this.getSelectedRuleAllowedFileTypesSelectionValue(),
      selectedRuleAllowedFileTypes,
    );
    this.selectedRuleAllowedFileTypesSelectAllOption?.setSelected(allSelectedRuleFileTypesSelected);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   NAVIGATION / SELECTION
  // ═══════════════════════════════════════════════════════════════════════════

  selectSection(section: KiEditorSection): void {
    this.selectedSection = section;
    this.selectedRuleKey = null;
  }

  selectRule(rule: KiRule): void {
    this.selectedSection = 'rule';
    this.selectedRuleKey = this.getRuleKey(rule);
  }

  getRuleKey(rule: KiRule): string {
    const persistentId = (rule.id ?? '').trim();
    if (persistentId) return persistentId;
    const existingLocalKey = this.localRuleKeys.get(rule);
    if (existingLocalKey) return existingLocalKey;
    const createdLocalKey = `local-${crypto.randomUUID()}`;
    this.localRuleKeys.set(rule, createdLocalKey);
    return createdLocalKey;
  }

  isRuleSelected(rule: KiRule): boolean {
    return this.selectedRuleKey === this.getRuleKey(rule);
  }

  isRuleMenuOpen(rule: KiRule): boolean {
    return this.ruleMenuOpenKey === this.getRuleKey(rule);
  }

  /** Regeln, die der aktuelle Vorschlag entfernt – existieren nicht mehr in `rules`, daher separat. */
  get removedProposalRules(): KiProposalRule[] {
    return this.proposalDiff?.ruleChanges.removed ?? [];
  }

  /** Die in der Seitenleiste angeklickte, vom Vorschlag entfernte Regel (zeigt rechts die Karte). */
  get selectedRemovedRule(): KiProposalRule | undefined {
    return this.removedProposalRules.find(rule => (rule.id ?? '') === this.selectedRuleKey);
  }

  selectRemovedRule(rule: KiProposalRule): void {
    this.selectedSection = 'removed-rule';
    this.selectedRuleKey = rule.id ?? '';
  }

  isRemovedRuleSelected(rule: KiProposalRule): boolean {
    return this.selectedSection === 'removed-rule' && this.selectedRuleKey === (rule.id ?? '');
  }

  /**
   * Verlässt den Quickstart und zeigt das Formular.
   *
   * <p>Lädt den myCheck dabei **neu**: der Agent hat ihn serverseitig gefüllt, das Formular hält
   * noch den Stand vom Öffnen. Ohne Neuladen zeigt der Editor leere Felder und ein „Übernehmen"
   * würde die Arbeit des Agenten überschreiben.
   */
  leaveQuickstart(reload: boolean = true): void {
    this.quickstartActive = false;
    this.chatService.closeMycheckSurface();
    this.chatService.forgetMycheckQuickstart(this.rulesetId);
    if (reload && this.rulesetId) {
      this.loadRuleset(this.rulesetId, null, false);
    }
  }

  ngOnInit(): void {
    // Vorgabemodell des Kunden; ein geladenes Regelset überschreibt es mit seinem `kiModel`.
    this.aiModel = this.stateConfigService.getDefaultAiModel();
    const rulesetId = this.route.snapshot.paramMap.get('rulesetId') ?? '';
    this.rulesetId = rulesetId;
    const preselectRuleId = this.route.snapshot.queryParamMap.get('ruleId');
    this.readOnly = this.route.snapshot.queryParamMap.get('readOnly') === 'true';
    this.adminGlobal = this.route.snapshot.data['adminGlobal'] === true;
    // Ohne Projektkontext (globaler Admin-Modus) keine projekt-spezifischen Dokumentklassen laden.
    if (!this.adminGlobal) {
      this.loadDocumentClasses();
    }
    this.initRuleTest();
    this.unregisterReview = this.proposalReview.register(this);

    // Vorschlags-Signal aus dem Edit-Modus-Chat: `loadAndApplyProposal` prüft selbst noch einmal
    // readOnly/adminGlobal/rulesetId, das Abonnement selbst ist deshalb unbedingt.
    this.proposalSubscription = this.chatService.mycheckProposalReceived.subscribe(event => {
      if (event.rulesetId === this.rulesetId) this.loadAndApplyProposal(event.proposalId);
    });

    if (rulesetId) {
      this.loadRuleset(rulesetId, preselectRuleId, true);
    } else {
      this.ruleset = {
        id: rulesetId,
        name: 'Neues myCheck',
        description: '',
        type: 'KI',
        ruleCount: 0,
        allowedFileTypes: [...this.availableFileTypes],
        checkMode: 'SINGLE',
        isPrebuilt: false
      };
      this.rules = [];
      this.initForm();
      this.restoreSetConfig({});
      this.reconcileRuleAllowedSelections();
      this.markSaved();
    }
  }

  /** Öffnet den Chat im Edit-Modus für dieses (bereits gespeicherte) myCheck. */
  openEditChat(): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    void this.chatService.openMycheckEditChat(this.rulesetId, 'KI');
  }

  /**
   * Lädt das myCheck und bindet das Formular neu.
   *
   * <p>Wird auch **nach** dem Quickstart gebraucht: der Agent füllt den myCheck serverseitig,
   * während der Editor noch den Stand vom Öffnen zeigt. Ohne dieses Neuladen sähe der Nutzer beim
   * Wechsel in die manuelle Bearbeitung das leere Formular – und ein „Übernehmen" würde die Arbeit
   * des Agenten damit überschreiben.
   *
   * @param initial true beim ersten Laden (startet zusätzlich den Quickstart).
   */
  /**
   * Selbstschutz gegen Deep-Links: `readOnly` kam bisher AUSSCHLIESSLICH aus dem Query-Parameter,
   * ein von Hand eingefügter oder gemerkter Link ohne `?readOnly=true` öffnete ein zentral
   * bereitgestelltes Regelwerk also voll editierbar. Bereitgestellte Sets sind nur zum Ansehen,
   * Ausführen und Duplizieren gedacht – die Bearbeitung läuft über den Admin-Bereich
   * (`adminGlobal`). Analog zu `MyChecksBuilderComponent`, das das für Prüfläufe schon tut.
   */
  private enforceReadOnlyForProvided(isProvided: boolean): void {
    if (this.adminGlobal || !isProvided) return;
    if (this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN')) return;
    this.readOnly = true;
  }

  /**
   * Selbstschutz wie {@link enforceReadOnlyForProvided}, für ein aus einem anderen Projekt geteiltes
   * Set: die Übersichten öffnen es schreibgeschützt, ein Deep-Link oder „Öffnen" im Builder aber
   * voll editierbar. Bearbeitet wird es nur im Eigentümerprojekt – auch mit Regelwerk-Admin-Recht.
   */
  private enforceReadOnlyForLinked(ownerProjectId: number | null | undefined): void {
    if (this.adminGlobal || ownerProjectId == null) return;
    const currentProjectId = this.stateConfigService.getProjectIdOrUndefined();
    if (currentProjectId == null || Number(ownerProjectId) === Number(currentProjectId)) return;
    this.linkedFromOtherProject = true;
    this.readOnly = true;
  }

  ngOnDestroy(): void {
    this.quickstartCompletedSubscription?.unsubscribe();
    this.proposalSubscription?.unsubscribe();
    this.ruleTestSubscriptions.unsubscribe();
    this.unregisterRuleTestEditor?.();
    this.unregisterReview?.();
    this.chatService.closeMycheckSurface();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   TESTLAUF („Prüfregel testen", #2356)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Testlauf nur im eigenen Projekt und im bearbeitbaren Stand: nicht schreibgeschützt (bereitgestellt
   * oder geteilt), nicht während des Quickstarts, nicht im Admin-Bereich (dort gibt es kein Projekt und
   * damit keine Projektdatei).
   */
  get canRuleTest(): boolean {
    return !!this.ruleTestKey && !this.readOnly && !this.quickstartActive && !this.adminGlobal && !this.loading;
  }

  get ruleTestLimitReached(): boolean {
    return this.ruleTestQuota?.remaining === 0;
  }

  /** Tooltip am deaktivierten Button (Figma 06); `undefined` blendet ihn aus – `''` zeigte einen leeren Pfeil. */
  get ruleTestTooltip(): string {
    return ruleTestLimitTooltip(this.ruleTestQuota)?.title ?? undefined as unknown as string;
  }

  get ruleTestTooltipSubtext(): string {
    return ruleTestLimitTooltip(this.ruleTestQuota)?.text ?? undefined as unknown as string;
  }

  private initRuleTest(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (this.adminGlobal || !projectId || !this.rulesetId) return;
    this.ruleTestKey = ruleTestContextKey(projectId, this.rulesetId);
    this.pendingRuleTestRunId = this.route.snapshot.queryParamMap.get('ruleTest');
    this.ruleTestSubscriptions.add(this.ruleTestService.quota$.subscribe(quota => this.ruleTestQuota = quota));
    this.ruleTestSubscriptions.add(
      this.ruleTestService.hasUnseenResult$(this.ruleTestKey).subscribe(unseen => this.ruleTestUnseen = unseen),
    );
    this.unregisterRuleTestEditor = this.ruleTestService.registerEditor(
      this.ruleTestKey, [(this.router.url ?? '').split('?')[0]], () => this.openRuleTest(),
    );
    this.ruleTestService.refreshQuota();
  }

  /** Nach dem Laden des Sets: ein über `?ruleTest=` mitgegebener Lauf öffnet direkt das Popup. */
  private openPendingRuleTest(): void {
    const runId = this.pendingRuleTestRunId;
    this.pendingRuleTestRunId = null;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!runId || !projectId || !this.canRuleTest) return;
    this.ruleTestService.attach(projectId, this.ruleTestKey, runId);
    this.openRuleTest();
  }

  openRuleTest(): void {
    if (!this.canRuleTest) return;
    this.ruleTestContext = this.buildRuleTestContext();
  }

  closeRuleTest(): void {
    this.ruleTestContext = null;
  }

  /**
   * Friert den aktuellen, ungespeicherten Editor-Stand ein: was danach im Editor geändert wird,
   * gilt erst beim nächsten Öffnen. Neue, noch nicht gespeicherte Regeln tragen im Snapshot ihre
   * lokale Editor-ID, damit `ruleKey` sie dort wiederfindet.
   */
  buildRuleTestContext(): RuleTestPopupContext {
    const projectId = this.stateConfigService.getProjectIdOrUndefined() ?? 0;
    const snapshot = structuredClone(this.buildKiDocumentSnapshot());
    snapshot.rules.forEach((rule, index) => {
      if (!(rule.id ?? '').trim()) rule.id = this.getRuleKey(this.rules[index]);
    });
    const setConfig = snapshot.configJson;
    const rules: RuleTestRuleOption[] = snapshot.rules.map(rule => ({
      key: rule.id ?? '',
      shortCode: rule.configJson.ruleShortCode ?? '',
      name: rule.name ?? '',
      allowedFileTypes: rule.configJson.allowedFileTypes ?? [],
      allowedDocumentClasses: rule.configJson.allowedDocumentClasses ?? [],
    }));
    // Die zuletzt geöffnete Regel – auch wenn gerade Systemprompt o. ä. offen ist (Frame 01).
    const selected = this.selectedRuleKey;
    const editorDirty = this.isDirty;
    const checkMode = (this.form.value.checkMode ?? 'SINGLE') as CheckMode;
    return {
      projectId,
      projectName: this.stateConfigService.getProject()?.name ?? '',
      rulesetType: 'KI',
      rulesetId: this.rulesetId,
      setLabel: snapshot.name || this.ruleset?.name || '',
      rules,
      preselectedRuleKey: rules.some(rule => rule.key === selected) ? selected : (rules[0]?.key ?? null),
      ruleLocked: false,
      setAllowedFileTypes: (setConfig['allowedFileTypes'] as string[] | undefined) ?? [],
      setAllowedDocumentClasses: (setConfig['allowedDocumentClasses'] as string[] | undefined) ?? [],
      editorDirty,
      modelLabel: this.aiModel || null,
      validationErrors: this.collectRuleTestErrors(),
      ruleValidationErrors: this.collectRuleTestErrorsByRule(),
      contextKey: this.ruleTestKey,
      buildRequest: (ruleKey, files) => ({
        rulesetType: 'KI',
        rulesetId: this.rulesetId,
        ruleKey,
        editorDirty,
        files,
        ki: snapshot,
      }),
      reportConfig: buildDefaultReportConfigForRuleset(
        this.rulesetId, 'KI', this.pruefebene, this.outputParameters, checkMode,
      ),
    };
  }

  /**
   * Was einen Testlauf blockiert. Strenger als das Speichern: leere Prompts und unbekannte
   * `{Variablen}` würden die KI mit einer Anweisung voller Lücken laufen lassen – ein Testergebnis
   * dazu wäre wertlos und kostete trotzdem einen Lauf. Die Speichervalidierung bleibt unverändert.
   */
  private collectRuleTestErrors(): string[] {
    const errors: string[] = [];
    if (this.rules.length === 0) errors.push('Das Prüfregelset enthält noch keine Prüfregel.');
    if (!this.systemprompt.trim()) errors.push('Der Systemprompt ist leer.');
    if (!this.pruefprompt.trim()) errors.push('Der Prüfprompt ist leer.');
    const known = this.promptVariables;
    for (const variable of findUnknownPromptVariables(this.systemprompt, known)) {
      errors.push(`Systemprompt: Die Variable ${variable} ist nicht definiert.`);
    }
    for (const variable of findUnknownPromptVariables(this.pruefprompt, known)) {
      errors.push(`Prüfprompt: Die Variable ${variable} ist nicht definiert.`);
    }
    errors.push(...this.getMissingOptionMessages());
    return errors;
  }

  /**
   * Fehlende Pflicht-Prüfparameter je Regel: sie sperren nur den Test DIESER Regel. Getestet wird
   * immer genau eine – eine unvollständige Nachbarregel darf das Iterieren an einer anderen nicht
   * blockieren.
   */
  private collectRuleTestErrorsByRule(): Record<string, string[]> {
    const byRule: Record<string, string[]> = {};
    for (const entry of this.getMissingRequiredRuleParameters()) {
      (byRule[entry.ruleKey] ??= []).push(entry.message);
    }
    return byRule;
  }

  /**
   * Erkennt einen laufenden Quickstart für dieses myCheck und schützt das Formular davor.
   *
   * <p>Gestartet wird der Quickstart auf der myChecks-Übersicht; hierher kommt der Nutzer nur, wenn
   * er zwischendurch selbst nachsehen will.
   */
  private initQuickstart(): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    if (!this.chatService.isMycheckQuickstartActive(this.rulesetId)) return;

    void this.verifyAndLockQuickstart(this.rulesetId);
  }

  /**
   * Prüft, ob der gemerkte Quickstart noch eine echte, laufende Session ist, bevor das Formular
   * gesperrt wird.
   *
   * <p>Der Aktiv-Merker allein reicht nicht: er überlebt auch, wenn der Nutzer den Quickstart durch
   * Wegnavigieren statt über „Übernehmen"/„Manuell bearbeiten" verlassen hat. Ohne diese Prüfung
   * blieb das Formular dann bei jedem erneuten Öffnen dauerhaft gesperrt, obwohl gar kein Agent
   * mehr daran arbeitet.
   */
  private async verifyAndLockQuickstart(rulesetId: string): Promise<void> {
    let resumable: boolean;
    try {
      resumable = await this.chatService.canResumeMycheckQuickstart(rulesetId);
    } catch {
      // Unklar (z.B. Drosselung durch ACS) ist nicht dasselbe wie "weg": der Merker bleibt, aber
      // das Formular sperrt sich in diesem Aufruf nicht fälschlich.
      return;
    }
    // Der Nutzer kann inzwischen einen anderen myCheck geöffnet haben.
    if (this.rulesetId !== rulesetId) return;
    if (!resumable) {
      this.chatService.forgetMycheckQuickstart(rulesetId);
      return;
    }

    this.quickstartActive = true;
    this.quickstartCompletedSubscription = this.chatService.mycheckRulesetCompleted.subscribe(() => {
      this.onQuickstartCompleted();
    });
  }

  /**
   * Der Quickstart hat den myCheck als vollständig gemeldet: Prozess beenden und in die
   * myChecks-Übersicht wechseln, wo er direkt gestartet werden kann.
   */
  /**
   * Der Quickstart ist fertig: Formular mit dem Ergebnis des Agenten neu laden und freigeben.
   * Kein Wegnavigieren – der Nutzer ist hier, weil er den myCheck ansehen will.
   */
  private onQuickstartCompleted(): void {
    this.leaveQuickstart(true);
    this.toastService.success('myCheck ist fertig – Formular aktualisiert');
  }

  /** @returns ob gespeichert wird – `false`, wenn die Validierung (oder ein laufendes Speichern) es verhindert. */
  save(): boolean {
    if (this.readOnly || this.saving) return false;
    this.showValidation = true;
    this.refreshValidation();
    const missingOptions = this.getMissingOptionMessages();
    if (missingOptions.length > 0) {
      this.toastService.warning(missingOptions[0]);
      return false;
    }
    if (this.validationErrors.length > 0) {
      this.toastService.warning('Bitte ergänze die Pflicht-Prüfparameter in den markierten Prüfregeln.');
      return false;
    }

    // Speichern mit offenem Vorschlag übernimmt ihn: das Formular enthält ihn bereits vollständig.
    // Als übernommen gilt er erst, wenn der PUT durch ist – scheitert die Validierung oder das
    // Speichern, bleibt er offen, und der nächste Speichern-Klick schließt ihn ab.
    const proposal = this.pendingProposal;

    const snapshot = this.buildKiDocumentSnapshot();
    const setConfig = snapshot.configJson;
    const rules = snapshot.rules.map(r => ({
      id: r.id,
      name: r.name,
      description: r.description,
      category: r.category,
      configJson: JSON.stringify(r.configJson),
    }));

    // Analog zum Hybrid-Editor: die Prüfschritte (aus dem Prüfprompt) per KI in stabile, kurze
    // Titel interpretieren und im Set persistieren (`aiStepInterpretation`). Diese dienen dem
    // Bericht als konsistente Überschriften der Detail-Prüfschritte (statt des pro Lauf volatilen
    // Modell-Titels).
    //
    // Das läuft NACH dem Speichern und nicht davor: der Aufruf geht über den Service Bus an ein
    // LLM (Timeout 90 s serverseitig), er hing also direkt im Speicherpfad und machte aus einem
    // billigen PUT eine minutenlange Wartezeit. Jetzt ist das Speichern sofort durch, die Titel
    // kommen per Nachtrag. Fällt der Nachtrag aus, bleiben die vorhandenen Fallbacks
    // (describeKiPruefschritte) — das Regelset ist trotzdem gespeichert.
    this.saving = true;
    this.persistKiRuleset(setConfig, rules, () => {
      this.markPendingProposalApplied(proposal);
      this.interpretStepsAfterSave(setConfig, rules);
    });
    return true;
  }

  /**
   * Schritt-Interpretation nach dem Speichern nachziehen und in einem zweiten, kleinen PUT
   * persistieren. Fehler werden geloggt, aber nicht als Speicherfehler gemeldet — gespeichert ist
   * zu diesem Zeitpunkt bereits alles.
   */
  private interpretStepsAfterSave(setConfig: any, rules: any[]): void {
    const stepDescriptors = this.describeKiPruefschritte();
    if (stepDescriptors.length === 0) return;

    this.workflowService.interpretRule(stepDescriptors, []).subscribe({
      next: ({steps}) => {
        const titles = (steps ?? []).map(s => (s ?? '').trim());
        if (!titles.some(t => t)) return;
        this.persistKiRuleset({...setConfig, aiStepInterpretation: titles}, rules);
      },
      error: err => console.error('Schritt-Interpretation fehlgeschlagen (Regelset ist gespeichert):', err),
    });
  }

  /**
   * Wird generisch vom `UnsavedChangesGuard` aufgerufen, wenn der Nutzer beim Wegnavigieren
   * „Verwerfen" wählt. Ohne den Vorschlags-Fall würde hier nur `initialState` auf den aktuellen
   * (bereits mit dem Vorschlag befüllten) Formularstand neu gesetzt – der Vorschlag also
   * stillschweigend als „gespeichert" behandelt, statt wirklich verworfen zu werden.
   */
  discardChanges(): DiscardOutcome {
    if (this.pendingProposal) {
      if (this.hasIndividuallyAcceptedChanges()) {
        // „Verwerfen" im Dialog: eigene, während des Vorschlags gemachte Änderungen gehen mit, nur die
        // übernommenen bleiben. Scheitert deren Speichern an der Validierung, bleibt der Nutzer hier.
        return this.keepAcceptedChangesAndLeave('snapshot') ? 'savedAccepted' : 'blocked';
      }
      this.onProposalDiscardClicked();
      return 'discarded';
    }
    this.initialState = this.serializeEditorState();
    return 'discarded';
  }

  /**
   * Banner „Änderungen übernehmen": markiert den Vorschlag serverseitig als APPLIED (Bookkeeping)
   * UND persistiert die Formularwerte über denselben Pfad wie der Speichern-Button
   * (`save()`/`persistKiRuleset`).
   *
   * Ohne den `save()`-Aufruf hier wirkte „Übernehmen" für den Nutzer wie ein abgeschlossener
   * Speichervorgang (der Banner verschwindet, der Vorschlag ist ja längst im Formular sichtbar) –
   * tatsächlich blieb das Formular nur dirty, und ein Reload ohne den separaten Klick auf
   * „Speichern" verwarf die Änderungen stillschweigend. Die Bookkeeping-Markierung (APPLIED) darf
   * dabei fehlschlagen, ohne dass die eigentliche Persistierung ausbleibt – das eine ist reine
   * Server-Buchführung, das andere der für den Nutzer sichtbare Effekt.
   */
  onProposalApplyClicked(): void {
    if (!this.pendingProposal || !this.stateConfigService.getProjectIdOrUndefined() || this.applyingProposalAction) return;
    this.save();
  }

  /**
   * Speichert das KI-Regelset.
   *
   * @param setConfig
   * @param rules
   * @param afterSave optionaler Nachtrag, der nach dem erfolgreichen PUT startet (Schritt-
   *   Interpretation). Wird beim Nachtrags-PUT selbst nicht gesetzt, sonst gäbe es eine Endlos-
   *   Schleife.
   */
  private persistKiRuleset(setConfig: any, rules: any[], afterSave?: () => void): void {
    const payload: CheckRuleSet = {
      id: this.ruleset.id,
      name: this.form.value.name ?? this.ruleset.name,
      description: this.form.value.description ?? this.ruleset.description,
      type: 'KI',
      prebuilt: this.ruleset.isPrebuilt,
      configJson: JSON.stringify(setConfig),
      rules: rules as any,
    };

    const save$ = this.adminGlobal
      ? this.rulesetService.updateGlobalKiRuleset(this.ruleset.id, payload)
      : this.rulesetService.updateKiRuleset(this.ruleset.id, payload);
    // Ohne afterSave ist das der Nachtrags-PUT (Schritt-Interpretation): dann NICHT navigieren und
    // keinen Toast zeigen — der Nutzer hat den Editor längst verlassen, eine Navigation würde ihn
    // aus der Ansicht reißen, in der er inzwischen arbeitet.
    const isPrimarySave = !!afterSave;
    save$.subscribe({
      next: () => {
        if (!isPrimarySave) return;
        this.markSaved();
        this.saving = false;
        // Nachtrag anstoßen, solange die Komponente noch lebt — die Anfrage läuft unabhängig davon
        // weiter, ob der Nutzer als nächstes speichert oder zurücknavigiert.
        afterSave?.();
        this.toastService.success('myCheck gespeichert');
      },
      error: (err) => {
        if (!isPrimarySave) {
          console.error('Nachtrag der Schritt-Interpretation fehlgeschlagen:', err);
          return;
        }
        this.saving = false;
        console.error('Fehler beim Speichern des Regelsets:', err);
        this.toastService.error('Speichern des myChecks fehlgeschlagen');
      },
    });
  }

  private markSaved(): void {
    this.initialState = this.serializeEditorState();
  }

  /**
   * Baut das Vorschlagsdokument, das `save()` tatsächlich persistiert (Set-`configJson` plus Regeln
   * mit je eigenem `configJson`) – als **Objekt**, nicht als String. Dient drei Zwecken: Grundlage für
   * `save()`, für den Dirty-Check über `serializeEditorState()` und als `current`-Argument für
   * `computeMyCheckDiff`. Public, damit der Chat-Vorschlags-Diff sie ohne
   * Duplikation der Editor-Internas aufrufen kann.
   */
  buildKiDocumentSnapshot(): KiProposalDocument {
    const f = this.form.value;
    return {
      name: f.name ?? this.ruleset.name,
      description: f.description ?? this.ruleset.description,
      configJson: {
        allowedDocumentClasses: this.parseStoredDocumentClasses(f.allowedDocumentClasses),
        allowedFileTypes: this.serializeAllowedFileTypes(f.allowedFileTypes),
        checkMode: f.checkMode ?? 'SINGLE',
        inputParameters: this.inputParameters,
        checkParameters: this.checkParameters,
        tags: this.tags,
        eingabeparameter: this.eingabeparameter,
        pruefebene: this.pruefebene,
        identifikatorParamId: this.identifikatorParamId,
        pruefebeneFilter: this.pruefebene !== 'gesamtdokument' ? this.pruefebeneFilter : '',
        kiModel: this.aiModel,
        systemprompt: this.systemprompt,
        pruefprompt: this.pruefprompt,
        outputParameters: this.outputParameters,
        contextFiles: this.contextFiles,
      },
      rules: this.rules.map(r => ({
        id: r.id,
        name: r.name,
        description: r.description,
        category: r.category,
        configJson: {
          ruleShortCode: this.normalizeRuleShortCode(r.ruleShortCode),
          allowedDocumentClasses: this.parseStoredDocumentClasses(r.allowedDocumentClasses),
          allowedFileTypes: this.serializeAllowedFileTypes(r.allowedFileTypes),
          tagValues: r.tagValues ?? {},
          parameterValues: r.parameterValues ?? {},
        },
      })),
    };
  }

  /**
   * „Entfernen" auf der Karte einer vom Vorschlag entfernten Regel: die Regel fehlt im Formular
   * bereits, hier wird nur die Entscheidung festgehalten (analog {@link onFieldAccepted}) – sie bleibt
   * auch bei einem späteren globalen „Verwerfen" entfernt – und die Karte samt „entfernt"-Marke
   * verschwindet.
   */
  onRemovedRuleAccepted(rule: KiProposalRule): void {
    const ruleId = rule.id ?? '';
    this.individuallyAcceptedRemovedRuleIds.add(ruleId);
    this.dropRemovedRuleFromDiff(ruleId);
    this.settleProposalIfResolved();
  }

  /**
   * „Wiederherstellen" auf der Karte: die Regel kommt unverändert (Stand vor dem Vorschlag) an ihre
   * alte Position zurück und ist danach eine ganz normale Regel des Formulars.
   */
  onRemovedRuleRestored(rule: KiProposalRule): void {
    const snapshot = this.preProposalSnapshot;
    if (!snapshot) return;
    const ruleId = rule.id ?? '';
    const original = snapshot.rules.find(r => (r.id ?? '').trim() === ruleId);
    if (!original) return;
    this.individuallyAcceptedRemovedRuleIds.delete(ruleId);
    this.dropRemovedRuleFromDiff(ruleId, false);

    const current = this.buildKiDocumentSnapshot();
    const originalIndex = snapshot.rules.indexOf(original);
    const rules = [...current.rules];
    rules.splice(Math.min(originalIndex, rules.length), 0, original);
    this.applyKiDocumentToEditor({...current, rules});

    const restored = this.rules.find(r => r.id === ruleId);
    if (restored) this.selectRule(restored);
    this.settleProposalIfResolved();
  }

  /**
   * True, solange der Vorschlag noch mindestens eine offene (weder einzeln übernommene noch
   * verworfene) Änderung enthält. Steuert den Banner „Änderungen übernehmen". Neu angelegte Regeln
   * haben keine Einzelentscheidung und gelten deshalb bis zum globalen Übernehmen/Verwerfen als offen.
   */
  get hasOpenProposalChanges(): boolean {
    const diff = this.proposalDiff;
    if (!diff) return false;
    return diff.fieldChanges.size > 0
      || diff.ruleChanges.added.length > 0
      || diff.ruleChanges.removed.length > 0
      || diff.ruleChanges.changed.size > 0;
  }

  /** Status einer offenen Feld-Änderung (für Badges außerhalb von `app-change-marker`), `null` ohne Änderung. */
  fieldChangeStatus(path: string): ProposalChangeStatus | null {
    const change = this.proposalDiff?.fieldChanges.get(path);
    return change ? (change.status ?? deriveChangeStatus(change)) : null;
  }

  /**
   * Übernehmen-Button eines einzelnen `app-change-marker` (Set-Feld, z. B. `configJson.kiModel`): der
   * Feldwert steht bereits korrekt im Formular (das hat `applyProposal` schon erledigt), hier wird nur
   * die Markierung entfernt und der Pfad vor einem späteren globalen „Verwerfen" geschützt.
   */
  onFieldAccepted(path: string): void {
    this.individuallyAcceptedPaths.add(path);
    this.proposalDiff?.fieldChanges.delete(path);
    this.settleProposalIfResolved();
  }

  /**
   * Parameterlisten, über deren Zeilen einzeln entschieden wird (Schlüssel = Feldname in `configJson`). Die
   * Sammelmarke `configJson.<Schlüssel>` in {@link proposalDiff} bleibt bestehen, bis jede Zeile der Liste
   * entschieden ist.
   */
  private readonly rowLists: Record<string, RowDecisions<object>> = {
    eingabeparameter: this.createRowList('eingabeparameter', () => this.eingabeparameter, rows => this.eingabeparameter = rows as typeof this.eingabeparameter),
    checkParameters: this.createRowList('checkParameters', () => this.checkParameters, rows => this.checkParameters = rows as typeof this.checkParameters),
    tags: this.createRowList('tags', () => this.tags, rows => this.tags = rows as typeof this.tags),
    outputParameters: this.createRowList('outputParameters', () => this.outputParameters, rows => this.outputParameters = rows as typeof this.outputParameters),
    contextFiles: this.createRowList('contextFiles', () => this.contextFiles, rows => this.contextFiles = rows as typeof this.contextFiles),
  };

  // ─── Listen: Entscheidung je Zeile ───────────────────────────────────────────

  /**
   * Schreibt einen Vorschlag in den Formularzustand, OHNE zu speichern (Änderungsmodell: Vorschlag →
   * Nutzer bestätigt explizit). `markSaved()` wird bewusst NICHT aufgerufen: `isDirty` bleibt true,
   * der bestehende Speichern-Button persistiert den Vorschlag über den normalen PUT-Pfad.
   */
  applyProposal(proposal: MyCheckProposal): void {
    // Ein noch offener Vorschlag (vom Backend inzwischen SUPERSEDED) steckt bereits im Formular. Erst
    // zurücksetzen, sonst wäre er der „Vorher"-Stand des neuen und ein Verwerfen fiele nicht auf den
    // gespeicherten Stand zurück. Einzeln übernommene Felder bleiben dabei erhalten.
    if (this.pendingProposal) {
      this.revertPendingProposal();
    }
    this.individuallyAcceptedPaths.clear();
    this.individuallyAcceptedRulePaths.clear();
    this.individuallyAcceptedRemovedRuleIds.clear();
    this.resetRowLists();
    this.ruleEntryDecisions.reset();
    this.preProposalSnapshot = this.buildKiDocumentSnapshot();
    const doc = normalizeKiProposalDocument(proposal.document as KiProposalDocument);
    this.applyKiDocumentToEditor(doc);
    // Verglichen wird, was der Editor nach dem Anwenden tatsächlich zeigt, nicht das rohe Vorschlagsdokument:
    // der Editor normalisiert beim Anwenden (Dateitypen, leere Werte, nicht angezeigte Felder). Mit dem rohen
    // Dokument meldete die Seitenleiste „geändert", obwohl im Abschnitt nichts markiert werden konnte.
    const applied = this.buildKiDocumentSnapshot();
    this.proposalDiff = computeMyCheckDiff(this.preProposalSnapshot, applied);
    this.initRowLists();
    this.initRuleEntryDecisions(this.preProposalSnapshot, applied);
    this.proposalSetEntryIds = {
      parameterValues: new Set(this.checkParameters.map(entry => entry.id)),
      tagValues: new Set(this.tags.map(entry => entry.id)),
    };
    this.pendingProposal = proposal;
    // Bleibt nach dem Anwenden nichts zu entscheiden (z. B. nur `{a: ''}` gegen `{}`), wird der Vorschlag
    // sofort abgeschlossen – sonst bliebe er ohne Banner und Leiste offen und käme nach einem Reload wieder.
    this.settleProposalIfResolved();
  }

  /**
   * Legt je geänderter Regel fest, welche Prüfparameter bzw. Tags offen sind. Ein Map-Pfad ohne echte
   * Abweichung (z. B. `{a: ''}` gegen `{}`) hat nichts zu entscheiden und wird sofort aus dem Diff entfernt.
   */
  private initRuleEntryDecisions(before: KiProposalDocument, after: KiProposalDocument): void {
    const diff = this.proposalDiff;
    if (!diff) return;
    for (const [ruleKey, paths] of [...diff.ruleChanges.changed]) {
      const beforeRule = before.rules.find(r => (r.id ?? '').trim() === ruleKey);
      const afterRule = after.rules.find(r => (r.id ?? '').trim() === ruleKey);
      for (const field of KiEditorComponent.RULE_MAP_FIELDS) {
        const path = this.ruleMapPath(field);
        if (!paths.includes(path)) continue;
        const open = this.ruleEntryDecisions.init(ruleKey, field, beforeRule?.configJson[field], afterRule?.configJson[field]);
        if (open === 0) this.removeRuleChangedPath(ruleKey, path);
      }
    }
  }

  /** Vorher/Nachher einer Zeile für `app-change-marker`, `undefined` wenn unverändert. */
  rowChange(key: string, row: object, index: number): MyCheckFieldChange | undefined {
    return this.rowLists[key].change(row, index);
  }

  /** Vorher/Nachher eines einzelnen Felds einer Zeile, `undefined` wenn das Feld unverändert ist. */
  rowFieldChange(key: string, row: object, index: number, field: string): MyCheckFieldChange | undefined {
    return this.rowLists[key].fieldChange(row, index, field);
  }

  /** Wartet diese Zeile noch auf ✓/✗? */
  rowPending(key: string, row: object): boolean {
    return this.rowLists[key].isPending(row);
  }

  /** Rechter Einzug der Spaltenüberschriften, solange die Liste ✓/✗-Spalten zeigt. */
  rowGutter(key: string): number {
    return this.rowLists[key].pending ? ProposalRowComponent.GUTTER_PX : 0;
  }

  /** Zeilen, die der Vorschlag aus der Liste entfernt (stehen nicht mehr im Formular). */
  removedRows(key: string): { before: { name?: string }; index: number }[] {
    return this.rowLists[key].removed as { before: { name?: string }; index: number }[];
  }

  acceptRow(key: string, row: object): void {
    this.rowLists[key].accept(row);
  }

  rejectRow(key: string, row: object): void {
    this.rowLists[key].reject(row);
  }

  acceptRowRemoval(key: string, entry: { before: object; index: number }): void {
    this.rowLists[key].acceptRemoval(entry);
  }

  rejectRowRemoval(key: string, entry: { before: object; index: number }): void {
    this.rowLists[key].rejectRemoval(entry);
  }

  /** Die Zeilen einer Liste aus {@link rowLists}, wie sie im Formular stehen (dieselben Objekte, die die Liste kennt). */
  private rowListRows(key: string): object[] {
    const rows: Record<string, object[]> = {
      eingabeparameter: this.eingabeparameter,
      checkParameters: this.checkParameters,
      tags: this.tags,
      outputParameters: this.outputParameters,
      contextFiles: this.contextFiles,
    };
    return rows[key] ?? [];
  }

  private createRowList(key: string, rows: () => object[], setRows: (rows: object[]) => void,
                        comparableRows?: () => object[]): RowDecisions<object> {
    return new RowDecisions<object>({rows, setRows, comparableRows, decided: () => this.onRowListDecided(key)});
  }

  private rowListPath(key: string): string {
    return `configJson.${key}`;
  }

  private resetRowLists(): void {
    Object.values(this.rowLists).forEach(list => list.reset());
  }

  private captureRowLists(): Record<string, object[]> {
    return Object.fromEntries(Object.entries(this.rowLists).map(([key, list]) => [key, list.captureRows()]));
  }

  private rebindRowLists(previousRows: Record<string, object[]>): void {
    for (const [key, list] of Object.entries(this.rowLists)) list.rebind(previousRows[key] ?? []);
  }

  private anyRowAccepted(): boolean {
    return Object.values(this.rowLists).some(list => list.accepted);
  }

  /** Berechnet nach dem Anwenden eines Vorschlags je Liste, welche Zeilen er neu anlegt, ändert oder entfernt. */
  private initRowLists(): void {
    const snapshot = this.preProposalSnapshot;
    this.resetRowLists();
    if (!snapshot) return;
    for (const [key, list] of Object.entries(this.rowLists)) {
      if (!this.proposalDiff?.fieldChanges.has(this.rowListPath(key))) continue;
      list.init((snapshot.configJson[key] ?? []) as object[]);
      this.syncRowList(key);
    }
  }

  /** Entfernt die Sammelmarke, sobald keine Zeile der Liste mehr offen ist. */
  private syncRowList(key: string): void {
    const list = this.rowLists[key];
    if (list.diff && !list.pending) {
      this.proposalDiff?.fieldChanges.delete(this.rowListPath(key));
      list.diff = null;
    }
  }

  private onRowListDecided(key: string): void {
    this.syncRowList(key);
    if (key === 'checkParameters') this.closeOrphanedRuleEntries('parameterValues', this.checkParameters);
    if (key === 'tags') this.closeOrphanedRuleEntries('tagValues', this.tags);
    this.settleProposalIfResolved();
  }

  /**
   * Nach einer Zeilen-Entscheidung auf Set-Ebene: Hat der Nutzer einen vom Vorschlag neu angelegten
   * Prüfparameter bzw. Tag verworfen, sind auch die Regelwerte, die der Vorschlag dafür setzt, verworfen –
   * wie bei „Alle verwerfen“. Sie werden auf den Stand vor dem Vorschlag zurückgesetzt (der Schlüssel
   * existierte dort nicht und fällt weg) und geschlossen; ohne das blieben verwaiste Werte in der Map und
   * würden beim nächsten Speichern geschrieben. Andere offene Einträge ohne Set-Gegenstück (z. B. ein
   * Schlüssel, den es im Set nie gab) bleiben offen und stehen schreibgeschützt nach der Liste.
   */
  private closeOrphanedRuleEntries(field: RuleMapField, setEntries: { id: string }[]): void {
    const diff = this.proposalDiff;
    if (!diff) return;
    const existing = new Set(setEntries.map(entry => entry.id));
    const snapshotIds = new Set(((field === 'parameterValues'
      ? this.preProposalSnapshot?.configJson['checkParameters']
      : this.preProposalSnapshot?.configJson['tags']) as { id: string }[] | undefined ?? []).map(entry => entry.id));
    const rejectedSetIds = [...this.proposalSetEntryIds[field]].filter(id => !existing.has(id) && !snapshotIds.has(id));
    if (rejectedSetIds.length === 0) return;
    const path = this.ruleMapPath(field);
    for (const [ruleKey, paths] of [...diff.ruleChanges.changed]) {
      if (!paths.includes(path)) continue;
      for (const key of this.ruleEntryDecisions.openKeys(ruleKey, field)) {
        if (!rejectedSetIds.includes(key)) continue;
        this.revertRuleEntryToSnapshot(ruleKey, field, key);
        if (this.ruleEntryDecisions.decide(ruleKey, field, key, 'rejected')) {
          this.removeRuleChangedPath(ruleKey, path);
        }
      }
    }
  }

  private hasIndividuallyAcceptedChanges(): boolean {
    return this.individuallyAcceptedPaths.size > 0
      || this.anyRowAccepted()
      || this.ruleEntryDecisions.hasAccepted()
      || this.individuallyAcceptedRemovedRuleIds.size > 0
      || [...this.individuallyAcceptedRulePaths.values()].some(paths => paths.size > 0);
  }

  private dropRemovedRuleFromDiff(ruleId: string, resetSelection = true): void {
    if (!this.proposalDiff) return;
    this.proposalDiff.ruleChanges.removed = this.proposalDiff.ruleChanges.removed.filter(r => (r.id ?? '') !== ruleId);
    if (resetSelection && this.selectedSection === 'removed-rule' && this.selectedRuleKey === ruleId) {
      this.selectSection('general');
    }
  }

  /** Nach Abschluss/Verwerfen des Vorschlags gibt es keine „entfernt"-Karten mehr – Auswahl zurücksetzen. */
  private resetRemovedRuleSelection(): void {
    if (this.selectedSection === 'removed-rule') {
      this.selectSection('general');
    }
  }

  /** Verwerfen-Button eines einzelnen `app-change-marker`: Feldwert auf den Vorher-Stand zurücksetzen. */
  onFieldRejected(path: string): void {
    this.individuallyAcceptedPaths.delete(path);
    this.proposalDiff?.fieldChanges.delete(path);
    this.revertFieldToSnapshot(path);
    this.settleProposalIfResolved();
  }

  /** Wie {@link onFieldAccepted}, aber für ein skalares Regelfeld (z. B. `name`). Prüfparameter und Tags
   *  werden je Schlüssel über {@link onRuleEntryAccepted} entschieden. */
  onRuleFieldAccepted(rule: KiRule, path: KiRuleScalarPath): void {
    const key = (rule.id ?? '').trim();
    if (!key) return;
    const acceptedPaths = this.individuallyAcceptedRulePaths.get(key) ?? new Set<string>();
    acceptedPaths.add(path);
    this.individuallyAcceptedRulePaths.set(key, acceptedPaths);
    this.removeRuleChangedPath(key, path);
    this.settleProposalIfResolved();
  }

  /** Wie {@link onFieldRejected}, aber für ein skalares Regelfeld (Prüfparameter/Tags: {@link onRuleEntryRejected}). */
  onRuleFieldRejected(rule: KiRule, path: KiRuleScalarPath): void {
    const key = (rule.id ?? '').trim();
    if (!key) return;
    this.individuallyAcceptedRulePaths.get(key)?.delete(path);
    this.removeRuleChangedPath(key, path);
    this.revertRuleFieldToSnapshot(key, path);
    this.settleProposalIfResolved();
  }

  /** ✓ an einem einzelnen Prüfparameter bzw. Tag einer Regel: Wert bleibt, Eintrag ist entschieden. */
  onRuleEntryAccepted(rule: KiRule, field: RuleMapField, entryKey: string): void {
    const key = (rule.id ?? '').trim();
    if (!key) return;
    this.decideRuleEntry(key, field, entryKey, 'accepted');
  }

  /** ✗ an einem einzelnen Prüfparameter bzw. Tag einer Regel: nur dieser Schlüssel zurück auf den Vorher-Wert. */
  onRuleEntryRejected(rule: KiRule, field: RuleMapField, entryKey: string): void {
    const key = (rule.id ?? '').trim();
    if (!key) return;
    this.revertRuleEntryToSnapshot(key, field, entryKey);
    this.decideRuleEntry(key, field, entryKey, 'rejected');
  }

  private decideRuleEntry(ruleKey: string, field: RuleMapField, entryKey: string, outcome: 'accepted' | 'rejected'): void {
    if (this.ruleEntryDecisions.decide(ruleKey, field, entryKey, outcome)) {
      this.removeRuleChangedPath(ruleKey, this.ruleMapPath(field));
    }
    this.settleProposalIfResolved();
  }

  private revertRuleEntryToSnapshot(ruleKey: string, field: RuleMapField, entryKey: string): void {
    if (!this.preProposalSnapshot) return;
    const before = this.preProposalSnapshot.rules.find(r => (r.id ?? '').trim() === ruleKey);
    const current = this.buildKiDocumentSnapshot();
    const patchedRules = current.rules.map(r => (r.id ?? '').trim() === ruleKey
      ? this.withRuleFieldValue(r, this.ruleMapPath(field),
        this.withMapEntries(r.configJson[field], before?.configJson[field], [entryKey]))
      : r);
    this.applyKiDocumentToEditor({...current, rules: patchedRules});
  }

  /** Kopie von `target`, in der die Schlüssel `keys` den Wert aus `source` haben (bzw. fehlen, wenn sie dort fehlen). */
  private withMapEntries(target: Record<string, string> | undefined, source: Record<string, string> | undefined,
                         keys: string[]): Record<string, string> {
    const result = {...(target ?? {})};
    for (const key of keys) {
      if (source && key in source) {
        result[key] = source[key];
      } else {
        delete result[key];
      }
    }
    return result;
  }

  private ruleMapPath(field: RuleMapField): string {
    return `configJson.${field}`;
  }

  private ruleMapField(path: string): RuleMapField | undefined {
    return KiEditorComponent.RULE_MAP_FIELDS.find(field => this.ruleMapPath(field) === path);
  }

  private static readonly RULE_MAP_FIELDS: readonly RuleMapField[] = ['tagValues', 'parameterValues'];

  /**
   * Bei einer Löschung über das Regelmenü, während ein Vorschlag offen ist: Die Regel ist weg, ihre offenen
   * Änderungen kann niemand mehr entscheiden. Sie fallen aus dem Diff (samt Einzelentscheidungen), sonst
   * bliebe {@link hasOpenProposalChanges} true, während Leiste und Banner nichts mehr anzeigen.
   */
  private dropDeletedRuleFromProposal(ruleKey: string): void {
    const diff = this.proposalDiff;
    if (!diff || !ruleKey) return;
    diff.ruleChanges.changed.delete(ruleKey);
    diff.ruleChanges.added = diff.ruleChanges.added.filter(added => (added.id ?? '').trim() !== ruleKey);
    this.ruleEntryDecisions.forgetRule(ruleKey);
    this.individuallyAcceptedRulePaths.delete(ruleKey);
    this.settleProposalIfResolved();
  }

  /**
   * Sind alle Änderungen des Vorschlags einzeln entschieden, wird er abgeschlossen – ohne den Banner gibt
   * es keinen „Übernehmen"-Klick mehr, der das übernähme. Mit übernommenen Änderungen werden diese
   * gespeichert und der Vorschlag als APPLIED markiert, sonst gilt er als verworfen. Ohne das bliebe er im
   * Backend offen und käme nach einem Reload erneut.
   */
  private settleProposalIfResolved(): void {
    if (!this.pendingProposal || this.hasOpenProposalChanges) return;
    if (this.hasIndividuallyAcceptedChanges()) {
      // Alles ist entschieden: es gibt nichts mehr zurückzunehmen, eigene Änderungen werden mitgespeichert.
      this.keepAcceptedChangesAndLeave('current');
    } else {
      this.onProposalDiscardClicked();
    }
  }

  private removeRuleChangedPath(ruleKey: string, path: string): void {
    const changedPaths = this.proposalDiff?.ruleChanges.changed.get(ruleKey);
    if (!changedPaths) return;
    const remaining = changedPaths.filter(p => p !== path);
    if (remaining.length > 0) {
      this.proposalDiff!.ruleChanges.changed.set(ruleKey, remaining);
    } else {
      this.proposalDiff!.ruleChanges.changed.delete(ruleKey);
    }
  }

  /** Liest einen Diff-Pfad aus einem `KiProposalDocument` – die Schreib-Variante von {@link readRuleFieldValue}
   *  für Set-Felder statt Regelfelder. */
  private readDocumentField(doc: KiProposalDocument, path: string): unknown {
    if (path === 'name' || path === 'description') return doc[path];
    const key = path.startsWith('configJson.') ? path.slice('configJson.'.length) : path;
    return (doc.configJson as Record<string, unknown>)[key];
  }

  /** Liefert `doc` mit genau einem auf `value` gesetzten Feld – der Rest bleibt unverändert. */
  private withDocumentField(doc: KiProposalDocument, path: string, value: unknown): KiProposalDocument {
    if (path === 'name' || path === 'description') return {...doc, [path]: value};
    const key = path.startsWith('configJson.') ? path.slice('configJson.'.length) : path;
    return {...doc, configJson: {...doc.configJson, [key]: value}};
  }

  /** Liefert `rule` mit genau einem auf `value` gesetzten Feld – der Rest bleibt unverändert. */
  private withRuleFieldValue(rule: KiProposalRule, path: string, value: unknown): KiProposalRule {
    if (path === 'name' || path === 'description' || path === 'category') {
      return {...rule, [path]: value} as KiProposalRule;
    }
    const key = path.startsWith('configJson.') ? path.slice('configJson.'.length) : path;
    return {...rule, configJson: {...rule.configJson, [key]: value}};
  }

  /**
   * Setzt EIN Set-Feld auf seinen Vorher-Wert zurück, ohne die übrigen (bereits angewandten oder schon
   * einzeln entschiedenen) Felder anzutasten: aktuellen Stand snapshotten, nur den einen Pfad patchen,
   * das gepatchte Dokument komplett neu anwenden (dieselbe Schreib-Logik wie beim ersten Anwenden).
   */
  private revertFieldToSnapshot(path: string): void {
    if (!this.preProposalSnapshot) return;
    const current = this.buildKiDocumentSnapshot();
    const patched = this.withDocumentField(current, path, this.readDocumentField(this.preProposalSnapshot, path));
    this.applyKiDocumentToEditor(patched);
  }

  /** Wie {@link revertFieldToSnapshot}, aber für ein einzelnes Feld einer einzelnen Regel. */
  private revertRuleFieldToSnapshot(ruleKey: string, path: string): void {
    if (!this.preProposalSnapshot) return;
    const before = this.preProposalSnapshot.rules.find(r => (r.id ?? '').trim() === ruleKey);
    if (!before) return;
    const current = this.buildKiDocumentSnapshot();
    const patchedRules = current.rules.map(r => (r.id ?? '').trim() === ruleKey
      ? this.withRuleFieldValue(r, path, this.readRuleFieldValue(before, path))
      : r);
    this.applyKiDocumentToEditor({...current, rules: patchedRules});
  }

  /**
   * Baut das Dokument für ein globales „Verwerfen" im Banner (bzw. für das Abschließen nach der letzten
   * Einzelentscheidung): der AKTUELLE Stand, in dem nur die noch offenen Änderungen des Vorschlags auf den
   * Vorher-Wert zurückgesetzt werden. Eigene Änderungen, die der Nutzer während des offenen Vorschlags
   * gemacht hat, bleiben so stehen – vom Snapshot aus gingen sie stillschweigend verloren. Einzeln
   * übernommene oder verworfene Änderungen stehen nicht mehr im Diff und bleiben, wie sie sind.
   */
  private buildRevertOpenChangesDocument(snapshot: KiProposalDocument, diff: MyCheckDiff): KiProposalDocument {
    const current = this.buildKiDocumentSnapshot();
    let result = current;
    for (const path of diff.fieldChanges.keys()) {
      const rowKey = Object.keys(this.rowLists).find(key => this.rowListPath(key) === path);
      const value = rowKey && this.rowLists[rowKey].diff
        ? this.rowLists[rowKey].revertPending(current.configJson[rowKey] as object[])
        : this.readDocumentField(snapshot, path);
      result = this.withDocumentField(result, path, value);
    }

    // Vom Vorschlag neu angelegte Regeln haben keine bzw. eine unbekannte `id` (siehe `computeMyCheckDiff`);
    // vom Nutzer selbst angelegte Regeln bekommen ihre `id` sofort vom Backend und bleiben.
    const addedIds = new Set(diff.ruleChanges.added.map(rule => (rule.id ?? '').trim()).filter(id => !!id));
    let rules = result.rules
      .filter(rule => {
        const id = (rule.id ?? '').trim();
        return !!id && !addedIds.has(id);
      })
      .map(rule => {
        const key = (rule.id ?? '').trim();
        const openPaths = diff.ruleChanges.changed.get(key);
        const before = openPaths?.length ? snapshot.rules.find(r => (r.id ?? '').trim() === key) : undefined;
        if (!openPaths || !before) return rule;
        return openPaths.reduce((r, path) => {
          // Prüfparameter/Tags: nur die noch offenen Schlüssel zurück, einzeln entschiedene bleiben.
          const field = this.ruleMapField(path);
          const value = field
            ? this.withMapEntries(r.configJson[field], before.configJson[field], this.ruleEntryDecisions.openKeys(key, field))
            : this.readRuleFieldValue(before, path);
          return this.withRuleFieldValue(r, path, value);
        }, rule);
      });
    // Noch offene Entfernungen kommen an ihre alte Position zurück (wie „Wiederherstellen" auf der Karte).
    for (const removed of diff.ruleChanges.removed) {
      const id = (removed.id ?? '').trim();
      const original = snapshot.rules.find(r => (r.id ?? '').trim() === id);
      if (!original || rules.some(r => (r.id ?? '').trim() === id)) continue;
      rules = [...rules];
      rules.splice(Math.min(snapshot.rules.indexOf(original), rules.length), 0, original);
    }
    return {...result, rules};
  }

  /**
   * Baut das Dokument für „Verwerfen" im Verlassen-Dialog: grundsätzlich der Vorher-Snapshot, AUSSER für
   * Pfade, die der Nutzer zuvor einzeln über einen `app-change-marker` bestätigt hat – die behalten
   * ihren aktuellen (übernommenen) Wert, statt von einem globalen Verwerfen mit zurückgesetzt zu
   * werden (siehe Klassenkommentar {@link individuallyAcceptedPaths}). Anders als
   * {@link buildRevertOpenChangesDocument} fallen hier auch eigene Änderungen weg: der Nutzer hat
   * „Verwerfen" gewählt, gespeichert wird nur, was er am Vorschlag ausdrücklich übernommen hat.
   */
  private buildPartialRevertDocument(snapshot: KiProposalDocument): KiProposalDocument {
    const current = this.buildKiDocumentSnapshot();
    let result = snapshot;
    for (const path of this.individuallyAcceptedPaths) {
      result = this.withDocumentField(result, path, this.readDocumentField(current, path));
    }
    for (const [key, list] of Object.entries(this.rowLists)) {
      // Eine fertig entschiedene Liste hat keinen Diff mehr; mit übernommenen Zeilen gilt ihr aktueller Stand.
      if (!list.diff && !list.accepted) continue;
      result = this.withDocumentField(result, this.rowListPath(key),
        list.revertPending(current.configJson[key] as object[]));
    }
    result = {
      ...result,
      // Einzeln bestätigte Entfernungen bleiben entfernt, statt vom Snapshot zurückgebracht zu werden.
      rules: result.rules.filter(rule => !this.individuallyAcceptedRemovedRuleIds.has((rule.id ?? '').trim())).map(rule => {
        const key = (rule.id ?? '').trim();
        const acceptedPaths = this.individuallyAcceptedRulePaths.get(key) ?? new Set<string>();
        const currentRule = current.rules.find(r => (r.id ?? '').trim() === key);
        if (!currentRule) return rule;
        const withAcceptedPaths = [...acceptedPaths].reduce(
          (r, path) => this.withRuleFieldValue(r, path, this.readRuleFieldValue(currentRule, path)),
          rule,
        );
        // Einzeln übernommene Prüfparameter/Tags: Snapshot-Map plus genau diese Schlüssel aus dem aktuellen Stand.
        return KiEditorComponent.RULE_MAP_FIELDS.reduce((r, field) => {
          const acceptedKeys = this.ruleEntryDecisions.acceptedKeys(key, field);
          if (acceptedPaths.has(this.ruleMapPath(field)) || acceptedKeys.length === 0) return r;
          return this.withRuleFieldValue(r, this.ruleMapPath(field),
            this.withMapEntries(r.configJson[field], currentRule.configJson[field], acceptedKeys));
        }, withAcceptedPaths);
      }),
    };
    return result;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   CHATBIC-VORSCHLAG (Edit-Modus)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Verlassen mit einzeln übernommenen Feldern: nur die noch offenen Änderungen werden verworfen, die
   * bestätigten Felder werden gespeichert. Ohne das gingen sie beim Verlassen verloren, obwohl der
   * Nutzer sie ausdrücklich übernommen hat. Der Vorschlag gilt dann als APPLIED (teilweise übernommen).
   *
   * Als APPLIED markiert erst {@link save} nach erfolgreichem Speichern: scheitert die Validierung oder der
   * PUT, bleibt der Vorschlag offen (ohne Markierungen), und der nächste Speichern-Klick schließt ihn ab –
   * sonst wäre er serverseitig erledigt, obwohl nichts gespeichert wurde.
   *
   * @param base `'current'` nimmt nur die noch offenen Änderungen zurück (eigene bleiben, siehe
   *   {@link buildRevertOpenChangesDocument}); `'snapshot'` (Verlassen-Dialog) verwirft auch eigene.
   * @returns ob gespeichert wird – `false`, wenn die Validierung das Speichern verhindert.
   */
  private keepAcceptedChangesAndLeave(base: 'current' | 'snapshot'): boolean {
    const proposal = this.pendingProposal;
    if (!proposal) return false;
    this.revertPendingProposal(base);
    this.pendingProposal = proposal;
    // Läuft über die Komponente hinaus weiter, wie der Nachtrags-PUT in `persistKiRuleset`.
    return this.save();
  }

  /**
   * Schließt einen Vorschlag als übernommen ab (Backend-Buchführung plus lokaler Zustand). Wird von
   * {@link save} nach erfolgreichem Speichern aufgerufen – egal ob über den Banner oder den Speichern-Button.
   * Den lokalen Zustand räumt es nur ab, wenn inzwischen kein neuerer Vorschlag angewendet wurde.
   */
  private markPendingProposalApplied(proposal: MyCheckProposal | null): void {
    if (!proposal) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (this.pendingProposal === proposal) {
      this.pendingProposal = null;
      this.proposalDiff = null;
      this.preProposalSnapshot = null;
      this.individuallyAcceptedPaths.clear();
      this.individuallyAcceptedRulePaths.clear();
      this.individuallyAcceptedRemovedRuleIds.clear();
      this.resetRowLists();
      this.ruleEntryDecisions.reset();
      this.resetRemovedRuleSelection();
    }
    if (!projectId) return;
    this.applyingProposalAction = true;
    this.proposalService.apply(this.rulesetId, proposal.id, projectId).subscribe({
      error: err => {
        console.error('Vorschlag konnte nicht als APPLIED markiert werden (wird trotzdem gespeichert):', err);
        this.applyingProposalAction = false;
      },
      complete: () => this.applyingProposalAction = false,
    });
  }

  /** Banner „Verwerfen" (und `discardChanges()` bei pending Vorschlag): Formular zurücksetzen + Backend informieren. */
  onProposalDiscardClicked(): void {
    const proposal = this.pendingProposal;
    if (!proposal) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    this.revertPendingProposal();
    if (projectId) {
      this.proposalService.discard(this.rulesetId, proposal.id, projectId).subscribe({
        error: err => console.error('Verwerfen im Backend fehlgeschlagen (Formular bereits zurückgesetzt):', err),
      });
    }
  }

  /** Konfigurationsfelder je Sidebar-Abschnitt – für den Änderungs-Punkt in der Sektionsnavigation
   *  (siehe {@link sectionHasProposalChange}). Muss mit den `configJson.…`-Pfaden übereinstimmen, die
   *  im Template tatsächlich per `app-change-marker` markiert sind. */
  private static readonly SECTION_CONFIG_PATHS: Record<string, string[]> = {
    general: ['name', 'description', 'configJson.allowedFileTypes', 'configJson.allowedDocumentClasses', 'configJson.checkMode'],
    parameters: ['configJson.pruefebene', 'configJson.identifikatorParamId', 'configJson.pruefebeneFilter',
      'configJson.eingabeparameter', 'configJson.checkParameters', 'configJson.tags'],
    systemprompt: ['configJson.kiModel', 'configJson.systemprompt', 'configJson.pruefprompt', 'configJson.contextFiles'],
    'output-format': ['configJson.outputParameters'],
  };

  /** Zeigt einen Änderungs-Punkt in der Sektionsnavigation, wenn eine der Sektion zugehörigen
   *  Konfigurationspfade im aktuellen Vorschlag abweicht – ohne dass der Nutzer jede Sektion einzeln
   *  öffnen muss, um herauszufinden, was chatBIC geändert hat. */
  sectionHasProposalChange(section: KiEditorSection): boolean {
    const paths = KiEditorComponent.SECTION_CONFIG_PATHS[section];
    if (!paths || !this.proposalDiff) return false;
    return paths.some(path => this.proposalDiff!.fieldChanges.has(path));
  }

  /** Status-Punkt eines Abschnitts in der Seitenleiste (`app-proposal-nav-marker`). */
  sectionNavStatus(section: KiEditorSection): ProposalChangeStatus | null {
    return this.sectionHasProposalChange(section) ? 'changed' : null;
  }

  /**
   * Regel-Sidebar: neu durch den aktuellen Vorschlag – ohne `id` oder mit einer `id`, die es vorher nicht
   * gab (beides führt `computeMyCheckDiff` unter `added`, und beides nimmt ein Verwerfen wieder heraus).
   */
  isProposalRuleAdded(rule: KiRule): boolean {
    const diff = this.proposalDiff;
    if (!diff) return false;
    const id = (rule.id ?? '').trim();
    return !id || diff.ruleChanges.added.some(added => (added.id ?? '').trim() === id);
  }

  /** Status-Punkt einer Regel in der Seitenleiste: neu vor geändert. */
  ruleNavStatus(rule: KiRule): ProposalChangeStatus | null {
    if (this.isProposalRuleAdded(rule)) return 'added';
    if (this.isProposalRuleChanged(rule)) return 'changed';
    return null;
  }

  /** Regel-Sidebar: durch den aktuellen Vorschlag geändert (mind. ein Feld weicht ab). */
  isProposalRuleChanged(rule: KiRule): boolean {
    const key = (rule.id ?? '').trim();
    return !!key && !!this.proposalDiff?.ruleChanges.changed.has(key);
  }

  /**
   * Vorher/Nachher für ein einzelnes Regelfeld (z. B. `configJson.parameterValues`), für
   * `app-change-marker` in der Regel-Detailansicht. `proposalDiff.ruleChanges.changed` kennt nur die
   * geänderten *Pfade* je Regel, nicht die Werte – die werden hier aus dem Snapshot (Vorher) und der
   * bereits angewandten Regel (Nachher) nachgeschlagen.
   */
  ruleFieldChange(rule: KiRule, path: string): MyCheckFieldChange | undefined {
    const key = (rule.id ?? '').trim();
    if (!key || !this.proposalDiff || !this.preProposalSnapshot) return undefined;
    if (!this.proposalDiff.ruleChanges.changed.get(key)?.includes(path)) return undefined;
    const before = this.preProposalSnapshot.rules.find(r => (r.id ?? '').trim() === key);
    if (!before) return undefined;
    const beforeValue = this.readRuleFieldValue(before, path);
    const afterValue = this.readRuleFieldValue(rule, path);
    // Gecacht, weil das Objekt an [change] haengt (siehe memo.ts). Inhalt als
    // Schluessel, weil Pfade wie configJson.parameterValues oder tagValues auf
    // Objekte/Arrays zeigen, die der Editor in place veraendern darf.
    return this.memo(`fieldChange:${key}:${path}`,
      [contentKey(beforeValue), contentKey(afterValue)],
      (): MyCheckFieldChange => ({before: beforeValue, after: afterValue}));
  }

  /**
   * Vorher/Nachher eines einzelnen Prüfparameters bzw. Tags einer Regel für `app-change-marker`,
   * `undefined`, wenn dieser Schlüssel nicht (mehr) offen ist. Gecacht wie {@link ruleFieldChange}.
   */
  ruleEntryChange(rule: KiRule, field: RuleMapField, entryKey: string): MapEntryChange | undefined {
    const change = this.computeRuleEntryChange(rule, field, entryKey);
    if (!change) return undefined;
    const ruleKey = (rule.id ?? '').trim();
    return this.memo(`entryChange:${ruleKey}:${field}:${entryKey}`,
      [change.before, change.after, change.status],
      (): MapEntryChange => change);
  }

  /**
   * Offene Prüfparameter bzw. Tags einer Regel, die nicht in der normalen Liste des Regel-Details stehen:
   * vom Vorschlag zu löschende (sie stehen nicht mehr in der Map, `value` ist der alte Wert) und neue bzw.
   * geänderte Werte zu einem Schlüssel ohne Prüfparameter/Tag im Set (z. B. ein vertippter Schlüssel im
   * Vorschlag oder ein inzwischen gelöschter Parameter, `value` ist der aktuelle Wert). Sie erscheinen nach
   * der Liste schreibgeschützt mit ✓/✗ – ohne sie stünden sie ohne Entscheidung im Zähler der Review-Leiste.
   * Reihenfolge wie im Stepper ({@link orderedOpenEntryKeys}). Der Name kommt aus dem aktuellen Set, sonst
   * aus dem Stand vor dem Vorschlag, sonst ist es der Schlüssel.
   */
  unlistedRuleEntries(rule: KiRule, field: RuleMapField): { key: string; name: string; value: string; change: MapEntryChange }[] {
    const ruleKey = (rule.id ?? '').trim();
    const setEntries = field === 'parameterValues' ? this.checkParameters : this.tags;
    const snapshotEntries = (field === 'parameterValues'
      ? this.preProposalSnapshot?.configJson['checkParameters']
      : this.preProposalSnapshot?.configJson['tags']) as { id: string; name?: string }[] | undefined;
    const entries = this.orderedOpenEntryKeys(rule, field).flatMap(key => {
      if (this.isRuleEntryListed(rule, field, key)) return [];
      const change = this.ruleEntryChange(rule, field, key);
      if (!change) return [];
      const name = setEntries.find(entry => entry.id === key)?.name
        || snapshotEntries?.find(entry => entry.id === key)?.name
        || key;
      const value = change.status === 'removed' ? change.before : change.after;
      return [{key, name, value: String(value ?? ''), change}];
    });
    // Gecacht, weil das Array im Template per @for durchlaufen und `change` an [change] gebunden wird.
    return this.memo(`unlistedEntries:${ruleKey}:${field}`, [...entries.flatMap(e => [e.key, e.name, e.value, e.change])], () => entries);
  }

  /**
   * Prüfparameter bzw. Tags, die der Vorschlag auf Set-Ebene löscht (Zeile „Wird gelöscht“ in der Liste
   * des Sets), deren Wert in dieser Regel er aber nicht selbst entfernt. Ohne diese Liste fehlte der Eintrag
   * im Regel-Detail ganz: er steht weder in der aktuellen Set-Liste noch hat er eine eigene Regel-Entscheidung.
   * Im Regel-Detail erscheint er schreibgeschützt als „Wird gelöscht“ mit dem aktuellen (alten) Wert.
   *
   * Die Entscheidung ist an die Set-Zeile gekoppelt (`removal`): ✓/✗ im Regel-Detail rufen
   * {@link acceptRowRemoval} bzw. {@link rejectRowRemoval} für genau diese Zeile. Eine eigene Entscheidung je
   * Regel gibt es nicht, weil der Parameter nur für alle Regeln zugleich gelöscht oder behalten werden kann –
   * so können Regel-Detail und Set-Ebene nie widersprüchlich entschieden sein, und der Zähler der
   * Review-Leiste zählt die Löschung weiter einmal (an der Set-Zeile).
   * Einträge mit eigener Regel-Entscheidung stehen in {@link unlistedRuleEntries} und fehlen hier.
   */
  setRemovedRuleEntries(rule: KiRule, field: RuleMapField): SetRemovedRuleEntry[] {
    const ruleKey = (rule.id ?? '').trim();
    const rowKey = field === 'parameterValues' ? 'checkParameters' : 'tags';
    const setIds = new Set((field === 'parameterValues' ? this.checkParameters : this.tags).map(entry => entry.id));
    const removals = this.removedRows(rowKey) as SetRemovedRuleEntry['removal'][];
    const entries = removals.flatMap((removal): SetRemovedRuleEntry[] => {
      const key = removal.before.id;
      if (!key || setIds.has(key)) return [];
      if (this.ruleEntryDecisions.initialStatus(ruleKey, field, key) !== undefined) return [];
      const value = String(rule[field]?.[key] ?? '');
      const change = this.memo(`setRemovedChange:${ruleKey}:${field}:${key}`, [value],
        (): MapEntryChange => ({before: value, after: undefined, status: 'removed'}));
      return [{key, name: removal.before.name?.trim() || key, value, change, removal}];
    });
    // Gecacht, weil das Array im Template per @for durchlaufen und `change` an [change] gebunden wird.
    return this.memo(`setRemovedEntries:${ruleKey}:${field}`,
      entries.flatMap(entry => [entry.key, entry.name, entry.value, entry.change, entry.removal]), () => entries);
  }

  /**
   * Ob ein Prüfparameter bzw. Tag in der normalen Liste des Regel-Details steht: Er hat einen Eintrag im Set
   * und ist beim Anwenden des Vorschlags nicht als „Wird gelöscht“ markiert worden. Alle anderen offenen
   * Einträge stehen darunter ({@link unlistedRuleEntries}).
   */
  isRuleEntryListed(rule: KiRule, field: RuleMapField, entryKey: string): boolean {
    const ruleKey = (rule.id ?? '').trim();
    const status = this.ruleEntryDecisions.initialStatus(ruleKey, field, entryKey);
    if (status === undefined) return true;
    if (status === 'removed') return false;
    return (field === 'parameterValues' ? this.checkParameters : this.tags).some(entry => entry.id === entryKey);
  }

  /**
   * Ungecachter Kern von {@link ruleEntryChange}. Vorher/Nachher kommen bei jedem Aufruf aus Snapshot und
   * aktueller Regel, der Status bleibt der beim Anwenden festgelegte (siehe
   * {@link RuleEntryDecisions.initialStatus}): Leert der Nutzer einen geänderten Wert, um selbst etwas
   * einzutragen, bleibt das Feld editierbar („Geändert“), statt als „Wird gelöscht“ unter die Liste zu
   * springen. Setzt er ihn von Hand auf den alten Stand zurück, bleibt der Eintrag offen und entscheidbar.
   */
  private computeRuleEntryChange(rule: KiRule, field: RuleMapField, entryKey: string): MapEntryChange | undefined {
    const ruleKey = (rule.id ?? '').trim();
    if (!ruleKey || !this.proposalDiff || !this.preProposalSnapshot) return undefined;
    const status = this.ruleEntryDecisions.initialStatus(ruleKey, field, entryKey);
    if (!status) return undefined;
    const beforeMap = this.preProposalSnapshot.rules.find(r => (r.id ?? '').trim() === ruleKey)?.configJson[field];
    return {before: beforeMap?.[entryKey], after: rule[field]?.[entryKey], status};
  }

  // ─── Review-Leiste im Edit-Chat (ProposalReviewHost, #2789) ─────────────────

  get reviewRulesetId(): string {
    return this.rulesetId;
  }

  proposalReviewContext(): ProposalReviewContext | null {
    if (!this.ruleset) return null;
    const rule = this.selectedSection === 'rule' ? this.selectedRule : undefined;
    return {mycheckName: this.ruleset.name, itemLabel: rule ? this.getRuleListLabel(rule) : undefined};
  }

  acceptAllProposalChanges(): void {
    this.onProposalApplyClicked();
  }

  discardAllProposalChanges(): void {
    this.onProposalDiscardClicked();
  }

  /**
   * Offene ✓/✗-Entscheidungen in der Reihenfolge der Seitenleiste: Abschnitte (Felder bzw. je offene
   * Zeile), übrige Set-Felder, Regeln in Sidebar-Reihenfolge (neue Regel = 1, geänderte je Feld bzw. je
   * Prüfparameter/Tag), entfernte Regeln. Invariante: leer genau dann, wenn {@link hasOpenProposalChanges}
   * false ist – deshalb zählt ein offener Pfad ohne einzeln sichtbare Zeile bzw. ohne offenen Schlüssel
   * als ein Eintrag.
   */
  proposalReviewSnapshot(): ProposalReviewSnapshot | null {
    const diff = this.proposalDiff;
    const proposal = this.pendingProposal;
    if (!proposal || !diff || !this.hasOpenProposalChanges) return null;
    const items: ProposalReviewItem[] = [];

    const fieldItem = (path: string, section: KiEditorSection): ProposalReviewItem => {
      const key = `field:${path}`;
      return {key, reveal: () => this.revealSection(section, key)};
    };
    const sectionPaths = new Set<string>();
    for (const [section, paths] of Object.entries(KiEditorComponent.SECTION_CONFIG_PATHS)) {
      for (const path of paths) {
        sectionPaths.add(path);
        if (!diff.fieldChanges.has(path)) continue;
        const rowKey = Object.keys(this.rowLists).find(key => this.rowListPath(key) === path);
        const rowItems = rowKey ? this.rowReviewItems(rowKey, section) : [];
        items.push(...(rowItems.length ? rowItems : [fieldItem(path, section)]));
      }
    }
    for (const path of diff.fieldChanges.keys()) {
      if (!sectionPaths.has(path)) items.push(fieldItem(path, 'general'));
    }

    for (const rule of [...this.rules].sort((a, b) => this.compareRules(a, b))) {
      if (this.isProposalRuleAdded(rule)) {
        items.push({key: `rule-added:${this.getRuleKey(rule)}`, reveal: () => this.selectRule(rule)});
        continue;
      }
      const ruleKey = (rule.id ?? '').trim();
      const paths = diff.ruleChanges.changed.get(ruleKey);
      if (!paths) continue;
      for (const path of [...paths].sort((a, b) => this.ruleFieldOrder(a) - this.ruleFieldOrder(b))) {
        const field = this.ruleMapField(path);
        const entryKeys = field ? this.orderedOpenEntryKeys(rule, field) : [];
        const keys = entryKeys.length ? entryKeys.map(entryKey => `rule:${ruleKey}:${path}:${entryKey}`) : [`rule:${ruleKey}:${path}`];
        for (const key of keys) {
          items.push({key, reveal: () => this.revealRule(rule, key)});
        }
      }
    }

    for (const removed of diff.ruleChanges.removed) {
      items.push({key: `rule-removed:${removed.id ?? ''}`, reveal: () => this.selectRemovedRule(removed)});
    }

    return {
      proposalId: proposal.id,
      items,
      stale: !!proposal.stale,
      busy: this.applyingProposalAction || this.saving,
    };
  }

  /** Offene Zeilen einer Liste (neu/geändert in Listenreihenfolge, dann entfernte) als Review-Einträge. */
  private rowReviewItems(rowKey: string, section: KiEditorSection): ProposalReviewItem[] {
    const list = this.rowLists[rowKey];
    if (!list.diff) return [];
    const rows = this.rowListRows(rowKey);
    const keys = [
      ...rows.flatMap((row, index) => list.isPending(row) ? [`row:${rowKey}:${index}`] : []),
      ...list.removed.map(entry => `row:${rowKey}:removed:${entry.index}`),
    ];
    return keys.map(key => ({key, reveal: () => this.revealSection(section, key)}));
  }

  /** Offene Prüfparameter/Tags einer Regel in Anzeigereihenfolge: wie die Liste im Set, zu löschende zuletzt. */
  private orderedOpenEntryKeys(rule: KiRule, field: RuleMapField): string[] {
    const ruleKey = (rule.id ?? '').trim();
    const setIds = (field === 'parameterValues' ? this.checkParameters : this.tags).map(entry => entry.id);
    // Liste im Set, dann Einträge ohne Set-Gegenstück, zu löschende zuletzt (wie im Regel-Detail).
    const rank = (entryKey: string): number => {
      if (this.ruleEntryDecisions.initialStatus(ruleKey, field, entryKey) === 'removed') return setIds.length + 1;
      const index = setIds.indexOf(entryKey);
      return index >= 0 ? index : setIds.length;
    };
    return [...this.ruleEntryDecisions.openKeys(ruleKey, field)].sort((a, b) => rank(a) - rank(b));
  }

  /** Reihenfolge der Regelfelder wie im Regel-Detail. */
  private ruleFieldOrder(path: string): number {
    const index = KiEditorComponent.RULE_FIELD_ORDER.indexOf(path);
    return index >= 0 ? index : KiEditorComponent.RULE_FIELD_ORDER.length;
  }

  private static readonly RULE_FIELD_ORDER: readonly string[] = [
    'configJson.ruleShortCode', 'name', 'description', 'configJson.allowedFileTypes',
    'configJson.allowedDocumentClasses', 'configJson.tagValues', 'configJson.parameterValues',
  ];

  private revealSection(section: KiEditorSection, key: string): void {
    this.selectSection(section);
    revealProposalTarget(key);
  }

  private revealRule(rule: KiRule, key: string): void {
    this.selectRule(rule);
    revealProposalTarget(key);
  }

  private loadRuleset(rulesetId: string, preselectRuleId: string | null, initial: boolean): void {
    // Bis das Regelset da ist, zeigte der Editor ein leeres Formular — nicht unterscheidbar von
    // „Regelset ist leer". Analog zum Workflow-Editor gibt es dafür jetzt einen Ladezustand.
    this.loading = true;
    this.rulesetService.getKiRuleset(rulesetId, undefined, this.adminGlobal).subscribe((regelSet: CheckRuleSet) => {
        let setConfig: any = {};
        try { setConfig = regelSet.configJson ? JSON.parse(regelSet.configJson) : {}; } catch { /* ignore */ }

        const allowedFileTypes = this.parseStoredAllowedFileTypes(setConfig.allowedFileTypes);
        this.ruleset = {
          id: regelSet.id ?? rulesetId,
          name: regelSet.name ?? 'myCheck',
          description: regelSet.description ?? '',
          type: 'KI',
          ruleCount: regelSet.rules?.length ?? 0,
          allowedFileTypes: allowedFileTypes.length ? allowedFileTypes : [...this.availableFileTypes],
          checkMode: setConfig.checkMode ?? 'SINGLE',
          isPrebuilt: regelSet.prebuilt ?? false,
        };
      this.enforceReadOnlyForProvided(!!regelSet.prebuilt || regelSet.projectId == null);
      this.enforceReadOnlyForLinked(regelSet.projectId);

        this.initForm();
      this.applyKiDocumentToEditor({
        name: this.ruleset.name,
        description: this.ruleset.description,
        configJson: setConfig,
        rules: (regelSet.rules ?? []).map((r: any) => this.parseRawProposalRule(r)),
      });
        this.markSaved();

        // Direkt zur verlinkten Regel springen, falls vorhanden
        if (preselectRuleId) {
          const target = this.rules.find(r => r.id === preselectRuleId);
          if (target) this.selectRule(target);
        }

      this.loading = false;
      if (initial) {
        this.initQuickstart();
        this.openPendingRuleTest();
      }
      // Ein zwischenzeitlich (z. B. vor einem Reload) erzeugter Vorschlag soll nicht verloren gehen –
      // die Erkennung darüber ist sonst ausschließlich an das Live-Chat-Signal gebunden.
      this.loadPendingProposalIfAny();
    }, () => {
      // Auch im Fehlerfall den Ladezustand beenden, sonst bleibt der Editor dauerhaft im Spinner.
      this.loading = false;
      this.toastService.error('myCheck konnte nicht geladen werden');
    });
  }

  private serializeEditorState(): string {
    return JSON.stringify(this.buildKiDocumentSnapshot());
  }

  /**
   * Parst eine rohe Server-Regel (Server-`configJson` als JSON-**String**) in das Vorschlagsschema
   * (`configJson` als Objekt) – der gemeinsame Nenner, den {@link toKiRule} sowohl beim Laden vom
   * Server als auch beim Anwenden eines chatBIC-Vorschlags konsumiert.
   */
  private parseRawProposalRule(r: any): KiProposalRule {
    let rc: any = {};
    try {
      rc = r.configJson ? JSON.parse(r.configJson) : {};
    } catch { /* ignore */
    }
    return {
      id: r.id ?? '',
      name: r.name ?? '',
      description: r.description ?? '',
      category: r.category,
      configJson: rc,
    };
  }

  /** Wandelt eine Vorschlagsregel (Objekt-`configJson`) in das flache `KiRule`-Editorschema. */
  private toKiRule(r: KiProposalRule): KiRule {
    const rc = r.configJson ?? {};
    return {
      id: r.id ?? '',
      name: r.name ?? '',
      ruleShortCode: this.normalizeRuleShortCode(rc.ruleShortCode),
      description: r.description ?? '',
      category: r.category ?? 'Benutzerdefiniert',
      allowedDocumentClasses: this.parseStoredDocumentClasses(rc.allowedDocumentClasses),
      allowedFileTypes: this.parseStoredAllowedFileTypes(rc.allowedFileTypes),
      tagValues: rc.tagValues ?? {},
      parameterValues: rc.parameterValues ?? {},
    };
  }

  /**
   * Schreibt ein `KiProposalDocument` (Server-Antwort nach dem Parsen, oder ein chatBIC-Vorschlag) in
   * den Formularzustand. Wird sowohl von `loadRuleset()` als auch vom Anwenden/Verwerfen eines
   * Vorschlags genutzt – dieselbe Mapping-Logik, nicht kopiert.
   */
  private applyKiDocumentToEditor(doc: KiProposalDocument): void {
    // restoreSetConfig legt Eingabe- und Ausgabeparameter als neue Objekte an; offene Zeilen-
    // Entscheidungen eines Vorschlags müssen danach an diesen hängen, nicht an den alten.
    const previousRows = this.captureRowLists();
    this.rules = doc.rules.map(r => this.toKiRule(r));
    this.form.patchValue({name: doc.name, description: doc.description}, {emitEvent: false});
    this.restoreSetConfig(doc.configJson);
    this.reconcileRuleAllowedSelections();
    this.rebindRowLists(previousRows);
  }

  /** Lädt einen offenen Vorschlag nach, falls beim Öffnen bzw. Neuladen bereits einer aussteht. */
  private loadPendingProposalIfAny(): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    this.proposalService.getLatestPending(this.rulesetId, projectId).subscribe({
      next: proposal => {
        if (proposal) this.applyProposal(proposal);
      },
      error: err => console.error('Offener Vorschlag konnte nicht geladen werden:', err),
    });
  }

  /** Reagiert auf das `mycheckProposalReceived`-Signal des Edit-Modus-Chats. */
  private loadAndApplyProposal(proposalId: string): void {
    if (this.adminGlobal || this.readOnly || !this.rulesetId) return;
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    this.proposalService.getById(this.rulesetId, proposalId, projectId).subscribe({
      next: proposal => {
        if (proposal.status === 'PENDING') this.applyProposal(proposal);
      },
      error: err => console.error('Vorschlag konnte nicht geladen werden:', err),
    });
  }

  /**
   * Nimmt den offenen Vorschlag zurück: mit `'current'` nur seine noch offenen Änderungen (eigene
   * Änderungen bleiben), mit `'snapshot'` zurück auf den Stand von vor dem Vorschlag plus einzeln
   * Übernommenes. Bewusst KEIN `markSaved()`: war der Editor vorher bereits dirty (eigene, noch
   * ungespeicherte Änderungen), soll das nach dem Verwerfen weiterhin so angezeigt werden – `isDirty`
   * vergleicht ohnehin wieder gegen `initialState`, das durch `applyProposal()` nie verändert wurde.
   */
  private revertPendingProposal(base: 'current' | 'snapshot' = 'current'): void {
    if (this.preProposalSnapshot) {
      this.applyKiDocumentToEditor(base === 'current' && this.proposalDiff
        ? this.buildRevertOpenChangesDocument(this.preProposalSnapshot, this.proposalDiff)
        : this.buildPartialRevertDocument(this.preProposalSnapshot));
    }
    this.pendingProposal = null;
    this.proposalDiff = null;
    this.preProposalSnapshot = null;
    this.individuallyAcceptedPaths.clear();
    this.individuallyAcceptedRulePaths.clear();
    this.individuallyAcceptedRemovedRuleIds.clear();
    this.resetRowLists();
    this.ruleEntryDecisions.reset();
    this.resetRemovedRuleSelection();
  }

  /**
   * Liest einen Diff-Pfad (`name`, `configJson.parameterValues`, …) sowohl von einer Vorschlagsregel
   * (Werte unter `configJson`) als auch von einer bereits angewandten `KiRule` (dieselben Schlüssel
   * flach) – beide tragen identische Feldnamen, nur mit/ohne die `configJson`-Verschachtelung.
   */
  private readRuleFieldValue(rule: KiRule | KiProposalRule, path: string): unknown {
    if (path === 'name' || path === 'description' || path === 'category') {
      return (rule as unknown as Record<string, unknown>)[path];
    }
    const key = path.startsWith('configJson.') ? path.slice('configJson.'.length) : path;
    const configJson = (rule as KiProposalRule).configJson;
    return configJson ? (configJson as Record<string, unknown>)[key] : (rule as unknown as Record<string, unknown>)[key];
  }

  /**
   * Zerlegt den set-weiten Prüfprompt in Prüfschritte („Step 1: … Step 2: …") und beschreibt sie
   * als `RuleStepDescription[]` für die KI-Interpretation. Die Schritte sind für alle Regeln des
   * Sets gleich (gemeinsamer Prompt); im Bericht werden sie den Antworten je Index zugeordnet.
   * Ohne „Step N:"-Marker gibt es keine Interpretation (Bericht nutzt dann die Heuristik).
   */
  private describeKiPruefschritte(): RuleStepDescription[] {
    // Erkennt „1. …", „1) …", „Schritt 1: …" und „Step 1: …". Vorher wurde ausschließlich auf
    // „Step N:" gesplittet – ein deutsch nummerierter Prüfprompt („1. Beleg-Relevanz") ergab
    // damit NULL Schritte, die Interpretation lief nie an und der Bericht hatte keine
    // Ersatz-Titel für Prüfschritte, die das Modell unbenannt lässt.
    return parsePruefpromptSteps(this.pruefprompt).map((step, index) => ({
      modul: step.title || `Schritt ${index + 1}`,
      details: step.details,
    }));
  }

  navigateBack(): void {
    this.router.navigate(['../../'], {relativeTo: this.route});
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   PER-RULE TAG / PARAMETER VALUE ACCESSORS
  // ═══════════════════════════════════════════════════════════════════════════

  getTagValue(tagId: string): string {
    return this.selectedRule?.tagValues?.[tagId] ?? '';
  }

  setTagValue(tagId: string, value: string): void {
    const rule = this.selectedRule;
    if (!rule) return;
    if (!rule.tagValues) rule.tagValues = {};
    rule.tagValues[tagId] = value;
  }

  getParamValue(paramId: string): string {
    return this.selectedRule?.parameterValues?.[paramId] ?? '';
  }

  setParamValue(paramId: string, value: string): void {
    const rule = this.selectedRule;
    if (!rule) return;
    if (!rule.parameterValues) rule.parameterValues = {};
    rule.parameterValues[paramId] = value;
    this.revalidateIfNeeded();
  }

  onSelectedRuleNameChange(value: string): void {
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    selectedRule.name = value;
    this.rules = [...this.rules];
    this.revalidateIfNeeded();
  }

  onSelectedRuleDescriptionChange(value: string): void {
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    selectedRule.description = value;
    this.rules = [...this.rules];
  }

  addRule(): void {
    if (!this.canAddRule) {
      this.showRuleCreationWarning();
      return;
    }
    const newRule = {
      name: '',
      description: '',
      category: 'Benutzerdefiniert',
    };

    this.rulesetService.addKiRule(this.ruleset.id, newRule as any, this.adminGlobal).subscribe({
      next: (savedRule) => {
        const kiRule: KiRule = {
          id: savedRule.id ?? '',
          name: savedRule.name ?? '',
          ruleShortCode: '',
          description: savedRule.description ?? '',
          category: savedRule.category ?? newRule.category,
          // Default wie beim Set: "Alle erlaubt" statt einer leeren (= optisch nicht ausgewählten) Liste.
          allowedDocumentClasses: this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id),
          allowedFileTypes: this.getRuleAllowedFileTypeOptions(),
          tagValues: {},
          parameterValues: {},
        };
        this.rules = [...this.rules, kiRule];
        this.selectRule(kiRule);
        this.revalidateIfNeeded();
      },
      error: (err) => console.error('Fehler beim Erstellen der Regel:', err),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   SAVE (Übernehmen)
  // ═══════════════════════════════════════════════════════════════════════════

  onAiModelChange(value: string): void {
    this.aiModel = value;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   RULE CRUD
  // ═══════════════════════════════════════════════════════════════════════════

  toggleRuleMenu(event: MouseEvent, rule: KiRule): void {
    event.stopPropagation();
    const key = this.getRuleKey(rule);
    this.ruleMenuOpenKey = this.ruleMenuOpenKey === key ? null : key;
  }

  /** Close rule menu when clicking outside */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.ruleMenuOpenKey) {
      this.ruleMenuOpenKey = null;
    }
    if (this.filterPopupVisible) {
      this.filterPopupVisible = false;
    }
  }

  /** Close popups on Escape key */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.filterPopupVisible) {
      this.filterPopupVisible = false;
    }
    if (this.ruleMenuOpenKey) {
      this.ruleMenuOpenKey = null;
    }
  }

  onSelectedRuleShortCodeChange(value: string): void {
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    const shortCode = this.normalizeRuleShortCode(value);
    selectedRule.ruleShortCode = shortCode;
    this.rules = [...this.rules];
  }

  updateSelectedRuleAllowedDocumentClasses(value: string[] | string): void {
    if (this.suppressNextSelectedRuleAllowedDocumentClassesChange) {
      this.suppressNextSelectedRuleAllowedDocumentClassesChange = false;
      return;
    }
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    const currentValues = Array.isArray(selectedRule.allowedDocumentClasses) ? selectedRule.allowedDocumentClasses : [];
    selectedRule.allowedDocumentClasses = this.normalizeMultiSelectValue(
      value,
      currentValues,
      this.availableDocumentClasses.map(documentClass => documentClass.id),
    );
    this.rules = [...this.rules];
  }

  updateSelectedRuleAllowedFileTypes(value: string[] | string): void {
    if (this.suppressNextSelectedRuleAllowedFileTypesChange) {
      this.suppressNextSelectedRuleAllowedFileTypesChange = false;
      return;
    }
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    const currentValues = Array.isArray(selectedRule.allowedFileTypes) ? selectedRule.allowedFileTypes : [];
    selectedRule.allowedFileTypes = this.normalizeMultiSelectValue(
      value,
      currentValues,
      this.availableFileTypes,
    );
    this.rules = [...this.rules];
  }

  getSelectedRuleAllowedDocumentClassesSelectionValue(): string[] {
    return Array.isArray(this.selectedRule?.allowedDocumentClasses) ? [...this.selectedRule.allowedDocumentClasses] : [];
  }

  getSelectedRuleAllowedFileTypesSelectionValue(): string[] {
    return Array.isArray(this.selectedRule?.allowedFileTypes) ? [...this.selectedRule.allowedFileTypes] : [];
  }

  getRuleAllowedDocumentClassOptions(): { id: string; name: string }[] {
    const allowedDocumentClasses = new Set(this.getEffectiveAllowedDocumentClasses());
    return this.availableDocumentClasses.filter(documentClass => allowedDocumentClasses.has(documentClass.id));
  }

  getRuleAllowedFileTypeOptions(): string[] {
    const allowedFileTypes = new Set(this.getEffectiveAllowedFileTypes());
    return this.availableFileTypes.filter(fileType => allowedFileTypes.has(fileType));
  }

  toggleAllSelectedRuleAllowedDocumentClasses(): void {
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    const availableDocumentClassIds = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    const currentValues = Array.isArray(selectedRule.allowedDocumentClasses) ? selectedRule.allowedDocumentClasses : [];
    selectedRule.allowedDocumentClasses = this.areAllValuesSelected(currentValues, availableDocumentClassIds)
      ? []
      : [...availableDocumentClassIds];
    this.suppressNextSelectedRuleAllowedDocumentClassesChange = true;
    this.rules = [...this.rules];
  }

  toggleAllSelectedRuleAllowedFileTypes(): void {
    const selectedRule = this.selectedRule;
    if (!selectedRule) return;
    const availableFileTypes = this.getRuleAllowedFileTypeOptions();
    const currentValues = Array.isArray(selectedRule.allowedFileTypes) ? selectedRule.allowedFileTypes : [];
    selectedRule.allowedFileTypes = this.areAllValuesSelected(currentValues, availableFileTypes)
      ? []
      : [...availableFileTypes];
    this.suppressNextSelectedRuleAllowedFileTypesChange = true;
    this.rules = [...this.rules];
  }

  updateAllowedDocumentClasses(value: string[] | string): void {
    if (this.suppressNextAllowedDocumentClassesChange) {
      this.suppressNextAllowedDocumentClassesChange = false;
      return;
    }
    const currentValues = this.getAllowedDocumentClassesValue();
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    const nextValue = this.normalizeMultiSelectValue(value, currentValues, allDocumentClassIds);
    // Leere Auswahl = keine Dokumentenklasse erlaubt: dann findet der myCheck keine Datei mehr.
    // Das Feld faellt deshalb auf „alle Klassen" zurueck, statt leer zu bleiben.
    this.form.get('allowedDocumentClasses')?.setValue(nextValue.length ? nextValue : [...allDocumentClassIds]);
    this.reconcileRuleAllowedSelections();
  }

  updateAllowedFileTypes(value: string[] | string): void {
    if (this.suppressNextAllowedFileTypesChange) {
      this.suppressNextAllowedFileTypesChange = false;
      return;
    }
    const nextValue = this.normalizeMultiSelectValue(value, this.getAllowedFileTypesValue(), this.availableFileTypes);
    this.form.get('allowedFileTypes')?.setValue(nextValue);
    this.reconcileRuleAllowedSelections();
  }

  /**
   * „Alle erlauben" schaltet nur EIN: eine leere Auswahl waere eine Einschraenkung auf nichts,
   * mit der der myCheck keine Datei mehr annimmt. Abwaehlen geschieht ueber die einzelnen Klassen,
   * und die letzte verbliebene Klasse faellt in updateAllowedDocumentClasses auf „alle" zurueck.
   */
  toggleAllAllowedDocumentClasses(): void {
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    if (allDocumentClassIds.length === 0) return;
    this.suppressNextAllowedDocumentClassesChange = true;
    this.form.get('allowedDocumentClasses')?.setValue([...allDocumentClassIds]);
  }

  toggleAllAllowedFileTypes(): void {
    const nextValue = this.areAllValuesSelected(this.getAllowedFileTypesValue(), this.availableFileTypes) ? [] : [...this.availableFileTypes];
    this.suppressNextAllowedFileTypesChange = true;
    this.form.get('allowedFileTypes')?.setValue(nextValue);
  }

  getAllowedDocumentClassesSelectionValue(): string[] {
    // Gecacht, weil das Ergebnis an [value] haengt (siehe memo.ts); Inhalt als
    // Schluessel, damit Formular-Aenderungen sicher ankommen.
    const value = this.getAllowedDocumentClassesValue();
    return this.memo('allowedDocumentClassesSelection', [contentKey(value)], () => [...value]);
  }

  getAllowedFileTypesSelectionValue(): string[] {
    // Wie getAllowedDocumentClassesSelectionValue.
    const value = this.getAllowedFileTypesValue();
    return this.memo('allowedFileTypesSelection', [contentKey(value)], () => [...value]);
  }

  getAllowedDocumentClassesSelectAllLabel(): string {
    return 'Alle erlauben';
  }

  getAllowedFileTypesSelectAllLabel(): string {
    return 'Alle erlauben';
  }

  areAllAllowedFileTypesSelected(): boolean {
    return this.areAllValuesSelected(this.getAllowedFileTypesValue(), this.availableFileTypes);
  }

  /**
   * Auch eine leere Auswahl gilt als „alle erlaubt": so liest die Ausfuehrung sie (keine
   * Einschraenkung), und ohne diesen Fall zeigte ein Projekt ganz ohne Dokumentenklassen
   * „Dokumentenklassen auswaehlen", also scheinbar eine Einschraenkung auf nichts.
   */
  areAllAllowedDocumentClassesSelected(): boolean {
    const selected = this.getAllowedDocumentClassesValue();
    if (selected.length === 0) return true;
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    return this.areAllValuesSelected(selected, allDocumentClassIds);
  }

  areAllSelectedRuleAllowedFileTypesSelected(): boolean {
    return this.areAllValuesSelected(this.getSelectedRuleAllowedFileTypesSelectionValue(), this.getRuleAllowedFileTypeOptions());
  }

  areAllSelectedRuleAllowedDocumentClassesSelected(): boolean {
    const availableDocumentClassIds = this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id);
    return this.areAllValuesSelected(this.getSelectedRuleAllowedDocumentClassesSelectionValue(), availableDocumentClassIds);
  }

  // ─── Tag Filter ─────────────────────────────────────────────────────────────

  toggleFilterPopup(): void {
    this.filterPopupVisible = !this.filterPopupVisible;
  }

  isTagFilterActive(tagId: string): boolean {
    return this.activeTagFilters.includes(tagId);
  }

  toggleTagFilter(tagId: string): void {
    if (this.activeTagFilters.includes(tagId)) {
      this.activeTagFilters = this.activeTagFilters.filter(id => id !== tagId);
    } else {
      this.activeTagFilters = [...this.activeTagFilters, tagId];
    }
  }

  isTagValueFilterActive(tagId: string, value: string): boolean {
    return this.activeTagValueFilters.get(tagId)?.has(value) ?? false;
  }

  toggleTagValueFilter(tagId: string, value: string): void {
    const current = this.activeTagValueFilters.get(tagId) ?? new Set<string>();
    if (current.has(value)) {
      current.delete(value);
      if (current.size === 0) {
        this.activeTagValueFilters.delete(tagId);
      }
    } else {
      current.add(value);
      this.activeTagValueFilters.set(tagId, current);
    }
    // Trigger change detection
    this.activeTagValueFilters = new Map(this.activeTagValueFilters);
  }

  hasActiveFilters(): boolean {
    return this.activeTagValueFilters.size > 0;
  }

  /** Total number of active filter values */
  get activeFilterCount(): number {
    let count = 0;
    for (const values of this.activeTagValueFilters.values()) {
      count += values.size;
    }
    return count;
  }

  /** Tags filtered for display in filter popup */
  get filteredFilterTags(): { id: string; name: string; dataType: string; necessity: string; options?: string[] }[] {
    return this.tags;
  }

  /** Get options for a tag filtered by search term */
  getFilteredOptions(tag: { id: string; name: string; options?: string[] }): string[] {
    const options = tag.options ?? [];
    if (!this.filterSearch.trim()) return options;
    const q = this.filterSearch.toLowerCase();
    return options.filter(o => o.toLowerCase().includes(q));
  }

  clearTagFilters(): void {
    this.activeTagFilters = [];
    this.activeTagValueFilters = new Map();
  }

  // ─── Rule import (Excel) ───────────────────────────────────────────────────

  openImportPopup(): void {
    this.importPopupVisible = true;
    this.importReplaceExisting = false;
  }

  closeImportPopup(): void {
    if (this.importingRules) return;
    this.importPopupVisible = false;
  }

  /**
   * Erzeugt die Import-Vorlage dynamisch auf Basis der aktuell im Regelset definierten Tags und
   * Prüfparameter, damit importierte Zeilen direkt den richtigen `tagValues`/`parameterValues`
   * zugeordnet werden können (Zuordnung beim Import erfolgt über den Spaltennamen, s. `importRulesFromRows`).
   * Formatierung (Farben/Schrift/Rahmen) folgt der BIC-CI-Vorlage.
   *
   * Enthält zusätzlich alle bereits angelegten Prüfregeln als Zeilen. Beim Import werden Zeilen,
   * deren Kürzel (bzw. Name, falls kein Kürzel vergeben ist) einer bereits vorhandenen Regel
   * entspricht, übersprungen statt erneut angelegt – so dient die Vorlage zugleich als Übersicht
   * und verhindert Duplikate, wenn sie unverändert wieder eingelesen wird.
   */
  async downloadRuleImportTemplate(): Promise<void> {
    const metaHeaders = ['Kürzel', 'Name', 'Beschreibung'];
    const tagHeaders = this.tags.map(tag => tag.name?.trim() || 'Tag');
    const paramHeaders = this.checkParameters.map(param => param.name?.trim() || 'Prüfparameter');
    const headers = [...metaHeaders, ...tagHeaders, ...paramHeaders];

    const existingRows = this.rules.map(rule => [
      this.normalizeRuleShortCode(rule.ruleShortCode),
      rule.name,
      rule.description ?? '',
      ...this.tags.map(tag => rule.tagValues?.[tag.id] ?? ''),
      ...this.checkParameters.map(param => rule.parameterValues?.[param.id] ?? ''),
    ]);

    const rowsData = [...existingRows];
    if (rowsData.length === 0) {
      rowsData.push(['', 'Neue_Regel_1', 'Beschreibung der Prüfregel', ...tagHeaders.map(() => ''), ...paramHeaders.map(() => '')]);
    }

    const {darkBlue, turquoise, turquoiseLight, white, darkText} = CI_COLORS;
    const thinSide: ExcelJS.Border = {style: 'thin', color: {argb: darkText}};
    const thinBorder: Partial<ExcelJS.Borders> = {top: thinSide, left: thinSide, bottom: thinSide, right: thinSide};

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Prüfregeln');

    sheet.mergeCells(1, 1, 1, headers.length);
    const titleCell = sheet.getCell(1, 1);
    titleCell.value = `Prüfregeln importieren – ${this.ruleset?.name ?? 'KI-Regelset'}`;
    titleCell.font = {name: 'Arial', size: 16, bold: true, color: {argb: white}};
    titleCell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: darkBlue}};
    titleCell.alignment = {horizontal: 'left', vertical: 'middle'};
    sheet.getRow(1).height = 34;

    sheet.mergeCells(2, 1, 2, headers.length);
    const subtitleCell = sheet.getCell(2, 1);
    subtitleCell.value = 'Bitte nur neue Zeilen ergänzen. Bereits vorhandene Prüfregeln werden beim Import übersprungen.';
    subtitleCell.font = {name: 'Arial', size: 10, italic: true, color: {argb: darkBlue}};
    subtitleCell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: turquoiseLight}};
    subtitleCell.alignment = {horizontal: 'left', vertical: 'middle'};
    sheet.getRow(2).height = 20;

    const headerRowIndex = 3;
    headers.forEach((header, colIdx) => {
      const cell = sheet.getCell(headerRowIndex, colIdx + 1);
      cell.value = header;
      cell.font = {name: 'Arial', size: 11, bold: true, color: {argb: white}};
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: turquoise}};
      cell.border = thinBorder;
      cell.alignment = {horizontal: 'center', vertical: 'middle'};
    });
    sheet.getRow(headerRowIndex).height = 24;

    rowsData.forEach((rowValues, rowIdx) => {
      const excelRowIndex = headerRowIndex + 1 + rowIdx;
      rowValues.forEach((value, colIdx) => {
        const cell = sheet.getCell(excelRowIndex, colIdx + 1);
        cell.value = value;
        cell.font = {name: 'Arial', size: 11, color: {argb: darkText}};
        cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: white}};
        cell.border = thinBorder;
        cell.alignment = {horizontal: 'left', vertical: 'middle', wrapText: true};
      });
    });

    headers.forEach((header, colIdx) => {
      sheet.getColumn(colIdx + 1).width = Math.max(26, header.length + 8);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const setName = (this.ruleset?.name ?? 'KI-Regelset').replace(/[\\/:*?"<>|]/g, '');
    link.download = `Vorlage Prüfregeln - ${setName}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  onImportFileSelected(file: File): void {
    if (!this.importReplaceExisting && !this.canCreateRule) {
      this.showRuleLimitWarning();
      return;
    }
    this.importingRules = true;
    file.arrayBuffer()
      .then(buffer => readRuleImportRows(buffer))
      .then(
        rows => {
          if (rows === null) {
            this.importingRules = false;
            this.toastService.error('Die Kopfzeile mit der Spalte "Name" wurde nicht gefunden. Bitte nutze die bereitgestellte Vorlage.');
            return;
          }
          this.importRulesFromRows(rows);
        },
        () => {
          this.importingRules = false;
          this.toastService.error('Die Datei konnte nicht gelesen werden. Bitte nutze die bereitgestellte Vorlage.');
        });
  }

  private buildImportColumnMaps(): { tagIdByHeader: Map<string, string>; paramIdByHeader: Map<string, string> } {
    const tagIdByHeader = new Map<string, string>();
    for (const tag of this.tags) {
      const header = (tag.name ?? '').trim().toLowerCase();
      if (header) tagIdByHeader.set(header, tag.id);
    }
    const paramIdByHeader = new Map<string, string>();
    for (const param of this.checkParameters) {
      const header = (param.name ?? '').trim().toLowerCase();
      if (header) paramIdByHeader.set(header, param.id);
    }
    return {tagIdByHeader, paramIdByHeader};
  }

  private extractImportRowValues(
    row: Record<string, unknown>,
    tagIdByHeader: Map<string, string>,
    paramIdByHeader: Map<string, string>,
  ): { tagValues: Record<string, string>; parameterValues: Record<string, string> } {
    const tagValues: Record<string, string> = {};
    const parameterValues: Record<string, string> = {};
    for (const [key, value] of Object.entries(row)) {
      const stringValue = String(value ?? '').trim();
      if (!stringValue) continue;
      const normalizedKey = key.trim().toLowerCase();
      const tagId = tagIdByHeader.get(normalizedKey);
      if (tagId) {
        tagValues[tagId] = stringValue;
        continue;
      }
      const paramId = paramIdByHeader.get(normalizedKey);
      if (paramId) parameterValues[paramId] = stringValue;
    }
    return {tagValues, parameterValues};
  }

  /**
   * Legt eine einzelne importierte Zeile als neue Prüfregel an und ruft anschließend `onDone`
   * (Erfolg wie Fehlschlag) auf, damit Aufrufer die Zeilen sequenziell abarbeiten können.
   */
  private createRuleFromImportRow(
    row: Record<string, unknown>,
    tagIdByHeader: Map<string, string>,
    paramIdByHeader: Map<string, string>,
    onDone: (rule?: KiRule) => void,
  ): void {
    const shortCode = this.normalizeRuleShortCode(row['Kürzel']);
    const name = String(row['Name'] ?? '').trim();
    const newRule = {
      name,
      description: String(row['Beschreibung'] ?? '').trim(),
      category: 'Benutzerdefiniert',
    };

    this.rulesetService.addKiRule(this.ruleset.id, newRule as any, this.adminGlobal).subscribe({
      next: (savedRule) => {
        const {tagValues, parameterValues} = this.extractImportRowValues(row, tagIdByHeader, paramIdByHeader);
        const kiRule: KiRule = {
          id: savedRule.id ?? '',
          name: savedRule.name ?? newRule.name,
          ruleShortCode: shortCode,
          description: savedRule.description ?? newRule.description,
          category: savedRule.category ?? newRule.category,
          allowedDocumentClasses: this.getRuleAllowedDocumentClassOptions().map(documentClass => documentClass.id),
          allowedFileTypes: this.getRuleAllowedFileTypeOptions(),
          tagValues,
          parameterValues,
        };
        this.rules = [...this.rules, kiRule];
        onDone(kiRule);
      },
      error: () => onDone(undefined),
    });
  }

  /**
   * Legt importierte Zeilen sequenziell als neue Prüfregeln an. Zeilen, deren Kürzel (bzw. Name,
   * falls kein Kürzel vergeben ist) einer bereits vorhandenen Regel entspricht (Export derselben
   * Vorlage, unverändert wieder importiert), werden übersprungen statt dupliziert – analog zu
   * `getRuleSortKey`. Tag- und Prüfparameter-Spalten werden über den (case-insensitiven)
   * Spaltennamen den aktuellen Tags/Prüfparametern des Regelsets zugeordnet; unbekannte Spalten
   * werden ignoriert.
   */
  private importRulesFromRows(rows: Record<string, unknown>[]): void {
    const validRows = rows.filter(row => String(row['Name'] ?? '').trim().length > 0);
    if (validRows.length === 0) {
      this.importingRules = false;
      this.toastService.warning('Die Datei enthält keine importierbaren Prüfregeln (Spalte "Name" fehlt oder ist leer).');
      return;
    }

    const maxImportCount = this.importReplaceExisting ? this.MAX_RULES : Math.max(0, this.MAX_RULES - this.rules.length);
    const limitedRows = validRows.slice(0, maxImportCount);
    const skippedByLimit = validRows.length - limitedRows.length;
    if (maxImportCount === 0) {
      this.importingRules = false;
      this.toastService.warning(`Es sind maximal ${this.MAX_RULES} Prüfregeln möglich.`);
      return;
    }
    if (skippedByLimit > 0) {
      this.toastService.warning(`Nur die ersten ${maxImportCount} Prüfregel(n) wurden importiert (maximal ${this.MAX_RULES}).`);
    }

    if (this.importReplaceExisting) {
      this.replaceAllRulesFromImportRows(limitedRows);
      return;
    }

    const {tagIdByHeader, paramIdByHeader} = this.buildImportColumnMaps();
    const existingKeys = new Set<string>();
    for (const rule of this.rules) {
      const key = this.getRuleSortKey(rule).toLowerCase();
      if (key) existingKeys.add(key);
    }
    const total = limitedRows.length;
    let imported = 0;
    let skipped = 0;
    let failed = 0;

    const importNext = (index: number): void => {
      if (index >= limitedRows.length) {
        this.importingRules = false;
        this.importPopupVisible = false;
        this.revalidateIfNeeded();
        if (imported > 0) {
          this.toastService.success(`${imported} von ${total} Prüfregeln erfolgreich importiert.`);
        }
        if (skipped > 0) {
          this.toastService.warning(`${skipped} bereits vorhandene Prüfregel(n) übersprungen.`);
        }
        if (failed > 0) {
          this.toastService.error(`${failed} Prüfregel(n) konnten nicht importiert werden.`);
        }
        return;
      }

      const row = limitedRows[index];
      const shortCode = this.normalizeRuleShortCode(row['Kürzel']);
      const name = String(row['Name'] ?? '').trim();
      const rowKey = (shortCode || name).toLowerCase();

      if (rowKey && existingKeys.has(rowKey)) {
        skipped++;
        importNext(index + 1);
        return;
      }

      this.createRuleFromImportRow(row, tagIdByHeader, paramIdByHeader, (rule) => {
        if (rule) {
          if (rowKey) existingKeys.add(rowKey);
          imported++;
        } else {
          failed++;
        }
        importNext(index + 1);
      });
    };

    importNext(0);
  }

  /**
   * Löscht zunächst ALLE bestehenden Prüfregeln des Regelsets und legt anschließend die
   * importierten Zeilen komplett neu an (kein Abgleich per Kürzel/Name mehr nötig). Wird über die
   * Checkbox {@link importReplaceExisting} in der Import-Popup aktiviert.
   */
  private replaceAllRulesFromImportRows(validRows: Record<string, unknown>[]): void {
    const rulesToDelete = [...this.rules];
    let deleteFailed = 0;

    const deleteNext = (index: number): void => {
      if (index >= rulesToDelete.length) {
        this.createAllRulesFromImportRows(validRows, deleteFailed);
        return;
      }
      const rule = rulesToDelete[index];
      this.rulesetService.deleteKiRule(rule.id, this.adminGlobal).subscribe({
        next: () => {
          this.rules = this.rules.filter(r => r.id !== rule.id);
          deleteNext(index + 1);
        },
        error: () => {
          deleteFailed++;
          deleteNext(index + 1);
        },
      });
    };

    deleteNext(0);
  }

  private createAllRulesFromImportRows(validRows: Record<string, unknown>[], deleteFailed: number): void {
    const {tagIdByHeader, paramIdByHeader} = this.buildImportColumnMaps();
    const total = validRows.length;
    let imported = 0;
    let failed = 0;

    const importNext = (index: number): void => {
      if (index >= validRows.length) {
        this.importingRules = false;
        this.importPopupVisible = false;
        this.revalidateIfNeeded();
        if (imported > 0) {
          this.toastService.success(`${imported} von ${total} Prüfregeln importiert, bestehende Prüfregeln wurden ersetzt.`);
        }
        if (failed > 0) {
          this.toastService.error(`${failed} Prüfregel(n) konnten nicht importiert werden.`);
        }
        if (deleteFailed > 0) {
          this.toastService.error(`${deleteFailed} bestehende Prüfregel(n) konnten nicht gelöscht werden.`);
        }
        return;
      }

      this.createRuleFromImportRow(validRows[index], tagIdByHeader, paramIdByHeader, (rule) => {
        if (rule) imported++; else failed++;
        importNext(index + 1);
      });
    };

    importNext(0);
  }

  duplicateRule(rule: KiRule): void {
    this.ruleMenuOpenKey = null;
    if (!this.canCreateRule) {
      this.showRuleLimitWarning();
      return;
    }
    const duplicatePayload = {
      name: `${rule.name} (Kopie)`,
      description: rule.description ?? '',
      category: rule.category ?? 'Benutzerdefiniert',
    };

    this.rulesetService.addKiRule(this.ruleset.id, duplicatePayload as any, this.adminGlobal).subscribe({
      next: (savedRule) => {
        const duplicatedRule: KiRule = {
          id: savedRule.id ?? '',
          name: savedRule.name ?? duplicatePayload.name,
          ruleShortCode: this.normalizeRuleShortCode(rule.ruleShortCode),
          description: savedRule.description ?? rule.description ?? '',
          category: savedRule.category ?? rule.category ?? 'Benutzerdefiniert',
          allowedDocumentClasses: [...(rule.allowedDocumentClasses ?? [])],
          allowedFileTypes: [...(rule.allowedFileTypes ?? [])],
          tagValues: {...(rule.tagValues ?? {})},
          parameterValues: {...(rule.parameterValues ?? {})},
        };
        this.rules = [...this.rules, duplicatedRule];
        this.selectRule(duplicatedRule);
        this.revalidateIfNeeded();
      },
      error: (err) => console.error('Fehler beim Duplizieren der Regel:', err),
    });
  }

  getRuleListLabel(rule: KiRule): string {
    return this.normalizeRuleShortCode(rule.ruleShortCode) || rule.name || 'Prüfregel';
  }

  /** Wie {@link getRuleListLabel}, aber für eine vom Vorschlag entfernte Regel (Kürzel steht in `configJson`). */
  getRemovedRuleListLabel(rule: KiProposalRule): string {
    return this.normalizeRuleShortCode(rule.configJson?.ruleShortCode) || rule.name || 'Prüfregel';
  }

  /** Titel der Entfernen-Karte: Kürzel und Name, damit klar ist, welche Regel wegfällt. */
  getRemovedRuleCardLabel(rule: KiProposalRule): string {
    const shortCode = this.normalizeRuleShortCode(rule.configJson?.ruleShortCode);
    return [shortCode, rule.name].filter(Boolean).join(' – ') || 'Prüfregel';
  }

  /**
   * Löschen einer Prüfregel anfragen. Bewusst mit Bestätigung: die Regel kann in myChecks
   * referenziert sein, die dadurch angepasst werden – das soll der Nutzer vorher sehen.
   */
  deleteRule(rule: KiRule): void {
    this.ruleMenuOpenKey = null;
    this.ruleToDelete = rule;
    this.deleteUsages = [];
    this.deleteUsagesLoading = true;
    this.rulesetService.findMyCheckUsages([rule.id]).subscribe({
      next: usages => {
        if (this.ruleToDelete?.id !== rule.id) return;
        this.deleteUsages = usages;
        this.deleteUsagesLoading = false;
      },
      error: () => {
        if (this.ruleToDelete?.id !== rule.id) return;
        this.deleteUsagesLoading = false;
      },
    });
  }

  cancelDeleteRule(): void {
    this.ruleToDelete = null;
    this.deleteUsages = [];
    this.deleteUsagesLoading = false;
  }

  confirmDeleteRule(): void {
    const rule = this.ruleToDelete;
    if (!rule) return;
    this.rulesetService.deleteKiRule(rule.id, this.adminGlobal).subscribe({
      next: () => {
        this.rules = this.rules.filter(r => r.id !== rule.id);
        if (this.selectedRuleKey === this.getRuleKey(rule)) {
          this.selectedRuleKey = null;
          this.selectedSection = 'general';
        }
        this.dropDeletedRuleFromProposal((rule.id ?? '').trim());
        this.revalidateIfNeeded();
        this.cancelDeleteRule();
      },
      error: (err) => {
        console.error('Fehler beim Löschen der Regel:', err);
        this.cancelDeleteRule();
      },
    });
  }

  removeCheckParameter(index: number): void {
    if (this.checkParameters.length <= 1) return;
    this.checkParameters = this.checkParameters.filter((_, i) => i !== index);
    this.revalidateIfNeeded();
  }

  private normalizeRuleShortCode(value: unknown): string {
    return String(value ?? '').trim().slice(0, KiEditorComponent.RULE_SHORT_CODE_MAX_LENGTH);
  }

  private hasUneditedEmptyRule(): boolean {
    return this.rules.some(rule =>
      this.normalizeRuleShortCode(rule.ruleShortCode).length === 0
      && (rule.name ?? '').trim().length === 0
      && (rule.description ?? '').trim().length === 0,
    );
  }

  private showRuleCreationWarning(): void {
    if (!this.canCreateRule) {
      this.showRuleLimitWarning();
      return;
    }
    this.toastService.warning('Bitte bearbeite zuerst die neue leere Prüfregel.');
  }

  private showRuleLimitWarning(): void {
    this.toastService.warning(`Es sind maximal ${this.MAX_RULES} Prüfregeln möglich.`);
  }

  addCheckParameter(): void {
    this.checkParameters = [...this.checkParameters, {
      id: crypto.randomUUID(),
      name: 'Neuer Prüfparameter',
      dataType: 'Text',
      necessity: 'Optional'
    }];
    this.revalidateIfNeeded();
  }

  onCheckParameterNecessityChange(
    param: { id: string; name: string; dataType: string; necessity: string },
    value: string,
  ): void {
    param.necessity = value;
    this.checkParameters = [...this.checkParameters];
    this.revalidateIfNeeded();
  }

  isRuleInvalid(rule: KiRule): boolean {
    return this.invalidRuleKeys.has(this.getRuleKey(rule));
  }

  private revalidateIfNeeded(): void {
    if (this.showValidation) {
      this.refreshValidation();
    }
  }

  updateEingabeparameterDataType(param: SetEingabeparameter, value: string): void {
    const dataType = this.isIdentifikator(param) ? 'Text' : this.normalizeDataType(value);
    this.eingabeparameter = this.eingabeparameter.map(p =>
      // Wechsel WEG von 'Auswahl' räumt die Optionen mit auf: sie hätten keine Bedeutung mehr,
      // würden aber weiter gespeichert und beim Zurückwechseln als Altstand wieder auftauchen.
      p.id === param.id
        ? {...p, dataType, options: needsOptions({dataType}) ? (p.options ?? []) : undefined}
        : p,
    );
  }

  /**
   * Öffnet den Optionen-Dialog für ein beliebiges Objekt mit Optionsliste.
   *
   * `kind` bestimmt nur, welche Array-Referenz beim Übernehmen erneuert wird (Change Detection) –
   * der Dialog selbst kennt ausschließlich `string[]`.
   */
  openOptionsPopup(kind: 'tag' | 'output' | 'check' | 'input', target: OptionsHolder): void {
    this.optionsPopupKind = kind;
    this.optionsPopupTarget = target;
    this.optionsPopupLabel = String(target?.name ?? '').trim();
    this.optionsPopupOptions = target?.options ?? [];
    this.optionsPopupVisible = true;
  }

  private getMissingRequiredRuleParameters(): { ruleKey: string; message: string }[] {
    const requiredCheckParameters = this.checkParameters.filter(
      param => param.necessity === KiEditorComponent.REQUIRED_NECESSITY,
    );

    if (requiredCheckParameters.length === 0 || this.rules.length === 0) {
      return [];
    }

    return this.rules.flatMap(rule => {
      const ruleKey = this.getRuleKey(rule);
      const ruleLabel = this.getRuleListLabel(rule) || 'Prüfregel';
      const parameterValues = rule.parameterValues ?? {};

      return requiredCheckParameters
        .filter(param => !String(parameterValues[param.id] ?? '').trim())
        .map(param => {
          const parameterName = param.name?.trim() || 'Prüfparameter';
          return {
            ruleKey,
            message: `Regel "${ruleLabel}": Prüfparameter "${parameterName}" ist ein Pflichtfeld.`,
          };
        });
    });
  }

  private getAllowedDocumentClassesValue(): string[] {
    const value = this.form.get('allowedDocumentClasses')?.value;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  private getAllowedFileTypesValue(): string[] {
    const value = this.form.get('allowedFileTypes')?.value;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  private getEffectiveAllowedDocumentClasses(): string[] {
    const selected = this.getAllowedDocumentClassesValue();
    if (selected.length > 0) return selected;
    return this.availableDocumentClasses.map(documentClass => documentClass.id);
  }

  private getEffectiveAllowedFileTypes(): string[] {
    const selected = this.getAllowedFileTypesValue();
    if (selected.length > 0) return selected;
    return [...this.availableFileTypes];
  }

  private reconcileRuleAllowedSelections(): void {
    const allowedDocumentClasses = new Set(this.getEffectiveAllowedDocumentClasses());
    const allowedFileTypes = new Set(this.getEffectiveAllowedFileTypes());
    this.rules = this.rules.map(rule => ({
      ...rule,
      allowedDocumentClasses: (rule.allowedDocumentClasses ?? []).filter(documentClass => allowedDocumentClasses.has(documentClass)),
      allowedFileTypes: (rule.allowedFileTypes ?? []).filter(fileType => allowedFileTypes.has(fileType)),
    }));
  }

  private normalizeMultiSelectValue(value: string[] | string, currentValues: string[], allValues: string[]): string[] {
    const selectedValues = Array.isArray(value) ? value : [value];
    const normalizedValues = [...new Set(selectedValues)];
    const nextAllSelected = this.areAllValuesSelected(normalizedValues, allValues);

    if (nextAllSelected) {
      return [...allValues];
    }

    return normalizedValues.filter(valueItem => allValues.includes(valueItem) || !currentValues.includes(valueItem));
  }

  private getRuleSortKey(rule: KiRule): string {
    return this.normalizeRuleShortCode(rule.ruleShortCode) || (rule.name ?? '').trim();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //   PARAMETER / TAG / OUTPUT CRUD
  // ═══════════════════════════════════════════════════════════════════════════

  // ─── Eingabeparameter (Set-Ebene) – analog Hybrid-Editor ────────────────────

  /** Eingabeparameter mit Namen – als Optionen für den Identifikator-Select. */
  get identifikatorOptions(): SetEingabeparameter[] {
    return this.eingabeparameter.filter(param => (param.name ?? '').trim().length > 0);
  }

  isIdentifikator(param: SetEingabeparameter): boolean {
    return !!this.identifikatorParamId && param.id === this.identifikatorParamId;
  }

  addEingabeparameter(): void {
    this.eingabeparameter = [
      ...this.eingabeparameter,
      {id: crypto.randomUUID(), name: '', beschreibung: '', dataType: 'Text'},
    ];
  }

  removeEingabeparameter(index: number): void {
    const removed = this.eingabeparameter[index];
    this.eingabeparameter = this.eingabeparameter.filter((_, i) => i !== index);
    if (removed && removed.id === this.identifikatorParamId) {
      this.identifikatorParamId = this.eingabeparameter[0]?.id ?? '';
    }
  }

  /** Identifikator setzen. Der Identifikator ist immer 'Text' (Positionsnummern mit führenden Nullen/Punkten). */
  setIdentifikator(paramId: string): void {
    this.identifikatorParamId = paramId;
    this.eingabeparameter = this.eingabeparameter.map(param =>
      param.id === paramId ? {...param, dataType: 'Text'} : param,
    );
  }

  onOptionsPopupClosed(): void {
    this.optionsPopupVisible = false;
    this.optionsPopupKind = null;
    this.optionsPopupTarget = null;
  }

  onPruefebeneChange(value: string | string[]): void {
    const next = normalizePruefebene(Array.isArray(value) ? value[0] : value);
    if (next === this.pruefebene) return;
    this.pruefebene = next;
    this.applyPruefebeneDefaults(next);
  }

  /**
   * Schlägt beim Wechsel der Prüfebene passende Eingabeparameter vor (z. B. LV-Position →
   * Ordnungszahl/Kurztext/…). Ersetzt dabei immer nur die zuvor vorgeschlagenen Preset-Parameter
   * (`isPreset`) durch die Vorschläge der neuen Prüfebene; vom Nutzer angelegte/umbenannte
   * Parameter bleiben unverändert erhalten. Bei Gesamtdokument gibt es keine Vorschläge, keinen
   * Identifikator und keinen Prüfebene-Filter.
   */
  private applyPruefebeneDefaults(pruefebene: Pruefebene): void {
    const customParams = this.eingabeparameter.filter(param => !param.isPreset);
    const defaultNames = PRUEFEBENE_DEFAULT_PARAMETERS[pruefebene];
    const presetParams: SetEingabeparameter[] = defaultNames.map(name => ({
      id: crypto.randomUUID(), name, beschreibung: '', dataType: 'Text', isPreset: true,
    }));
    this.eingabeparameter = [...presetParams, ...customParams];

    if (pruefebene === 'gesamtdokument') {
      this.identifikatorParamId = '';
      this.pruefebeneFilter = '';
      return;
    }
    if (!this.eingabeparameter.some(param => param.id === this.identifikatorParamId)) {
      this.identifikatorParamId = this.eingabeparameter[0]?.id ?? '';
    }
  }

  private normalizeDataType(value: unknown): string {
    const candidate = String(value ?? '').trim();
    return (this.eingabeParameterTypes as readonly string[]).includes(candidate) ? candidate : 'Text';
  }

  /**
   * Lädt die Set-Eingabeparameter (positionsweise extrahierte Identifikatoren/Werte, Strukturebene).
   *
   * Bewusst KEINE Migration aus `inputParameters`: Letztere sind datei-/dokumentweite Datenquellen
   * (Dateiname/Dateitext/…) für die {Platzhalter}-Belegung des Prompts – sie taugen NICHT als
   * positionsweise extrahierbare Parameter (die Extraktion je LV-Position/Kapitel liefert dafür keine
   * sinnvollen Werte und der Identifikator bliebe leer). Alt-Sets ohne definierte `eingabeparameter`
   * bleiben daher im Gesamtdokument-/3-Spalten-Verhalten, bis im Editor echte Positions-Parameter
   * (z. B. Ordnungszahl, Kurztext) angelegt werden.
   */
  private restoreEingabeparameter(config: any): SetEingabeparameter[] {
    const raw = Array.isArray(config?.eingabeparameter) ? config.eingabeparameter : null;
    if (!raw || !raw.length) return [];
    return raw.map((p: any) => ({
      id: typeof p?.id === 'string' && p.id ? p.id : crypto.randomUUID(),
      name: String(p?.name ?? ''),
      beschreibung: String(p?.beschreibung ?? ''),
      dataType: this.normalizeDataType(p?.dataType ?? p?.typ),
      isPreset: !!p?.isPreset,
      // Ohne die Optionen stünde ein gespeicherter Auswahl-Parameter nach dem Laden ohne Werte da,
      // und getMissingOptionMessages() blockierte jedes weitere Speichern.
      ...(Array.isArray(p?.options) ? {options: p.options.map((o: any) => String(o ?? ''))} : {}),
    }));
  }

  addInputParameter(): void {
    this.inputParameters = [...this.inputParameters, {id: crypto.randomUUID(), name: '', dataType: 'Text', necessity: 'Optional'}];
  }

  removeInputParameter(index: number): void {
    this.inputParameters = this.inputParameters.filter((_, i) => i !== index);
  }
  private systemPromptCursorStart: number | null = null;

  private areAllValuesSelected(selectedValues: string[], allValues: string[]): boolean {
    return allValues.length > 0 && allValues.every(option => selectedValues.includes(option));
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: [this.ruleset.name],
      description: [this.ruleset.description],
      allowedDocumentClasses: [[]],
      allowedFileTypes: [this.ruleset.allowedFileTypes],
      checkMode: [this.ruleset.checkMode],
    });
  }

  addTag(): void {
    this.tags = [...this.tags, {id: crypto.randomUUID(), name: '', dataType: 'Auswahl', necessity: 'Optional', options: []}];
  }

  removeTag(index: number): void {
    this.tags = this.tags.filter((_, i) => i !== index);
  }

  addOutputParameter(): void {
    this.outputParameters = [...this.outputParameters, {modus: 'KI-Generiert', name: '', wert: '', dateityp: 'Text'}];
  }

  removeOutputParameter(index: number): void {
    this.outputParameters = this.outputParameters.filter((_, i) => i !== index);
  }
  private systemPromptCursorEnd: number | null = null;
  private systemPromptElement?: HTMLTextAreaElement;
  private pruefPromptCursorStart: number | null = null;

  onOptionsPopupSaved(options: string[]): void {
    if (this.optionsPopupTarget) {
      this.optionsPopupTarget.options = options;
      // Neue Array-Referenz je Liste, damit die Zeile die Chips sofort zeigt.
      switch (this.optionsPopupKind) {
        case 'tag':
          this.tags = [...this.tags];
          break;
        case 'output':
          this.outputParameters = [...this.outputParameters];
          break;
        case 'check':
          this.checkParameters = [...this.checkParameters];
          break;
        case 'input':
          this.eingabeparameter = [...this.eingabeparameter];
          break;
      }
      if (this.showValidation) this.refreshValidation();
    }
    this.onOptionsPopupClosed();
  }

  /** Chips-Vorschau der konfigurierten Optionen in der Parameterzeile. */
  optionsPreview(param: OptionsHolder | null | undefined): string[] {
    return (param?.options ?? []).filter(option => String(option ?? '').trim());
  }

  /** Ob der Parameter eine Optionsliste braucht (Datentyp „Auswahl"). */
  needsOptions(param: { dataType?: string; dateityp?: string } | null | undefined): boolean {
    return needsOptions(param);
  }

  /**
   * Auswahl-Parameter ohne Optionen. Blockiert das Speichern, weil der Datentyp zur Laufzeit sonst
   * still auf freien Text zurückfällt – die Regel prüfte dann etwas anderes als konfiguriert.
   */
  private getMissingOptionMessages(): string[] {
    return collectMissingOptionMessages([
      {label: 'Ausgabeparameter', params: this.outputParameters},
      {label: 'Prüfparameter', params: this.checkParameters},
      {label: 'Eingabeparameter', params: this.eingabeparameter},
      {label: 'Tag', params: this.tags},
    ]);
  }

  private refreshValidation(): void {
    const missingRequiredRuleParameters = this.getMissingRequiredRuleParameters();
    this.invalidRuleKeys = new Set(missingRequiredRuleParameters.map(entry => entry.ruleKey));
    this.validationErrors = [
      ...this.getMissingOptionMessages(),
      ...missingRequiredRuleParameters.map(entry => entry.message),
    ];
  }

  /**
   * Öffnet die gemeinsame Dateiauswahl (`app-context-file-picker`). Laden, Ladeanimation und
   * Fehler-Toast übernimmt der Picker; schlägt das Laden fehl, schließt er sich selbst (`closed`).
   */
  addContextFile(): void {
    this.filePickerVisible = true;
  }

  /** Max context files allowed */
  readonly MAX_CONTEXT_FILES = 20;

  /**
   * IDs der vorhandenen Kontextdateien für den Picker („bereits hinzugefügt"). Bewusst inkl. leerer
   * IDs (Altbestände ohne `id`), damit sie gegen `MAX_CONTEXT_FILES` zählen; eine leere ID passt
   * auf keine Picker-Datei, weil der Picker nur Dateien mit ID listet.
   */
  get contextFileIds(): string[] {
    return this.contextFiles.map(f => f.id);
  }

  /** Hängt die im Picker gewählten Dateien als `Kontext_<n>` an; schon vorhandene IDs werden übersprungen. */
  onContextFilesPicked(files: ProjectFile[]): void {
    for (const file of files) {
      if (file.id && this.contextFiles.some(cf => cf.id === file.id)) continue;
      const nextIndex = this.contextFiles.length + 1;
      const sizeStr = file.size ? (file.size / (1024 * 1024)).toFixed(1) + ' MB' : '';
      const dateStr = file.lastModified ? new Date(file.lastModified).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'}) + ', ' + new Date(file.lastModified).toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'}) : '';
      this.contextFiles = [...this.contextFiles, {
        id: file.id ?? '',
        label: `Kontext_${nextIndex}`,
        name: file.name ?? '',
        type: (file.fileType ?? '').toUpperCase(),
        size: sizeStr,
        date: dateStr,
      }];
    }
    this.filePickerVisible = false;
  }

  closeFilePicker(): void {
    this.filePickerVisible = false;
  }

  removeContextFile(index: number): void {
    this.contextFiles = this.contextFiles.filter((_, i) => i !== index);
    this.contextFiles = this.contextFiles.map((file, i) => ({...file, label: `Kontext_${i + 1}`}));
  }





  // ─── Two-way binding helpers for textareas ──────────────────────────────────
  private pruefPromptCursorEnd: number | null = null;
  private pruefPromptElement?: HTMLTextAreaElement;

  isProtectedInputParameter(param: { id: string; name: string; dataType: string; necessity: string }): boolean {
    const normalized = param.name?.trim().toLowerCase();
    return normalized === 'dateitext' || normalized === 'dateiname';
  }

  onOutputModusChange(param: { modus: string; name: string; wert: string; dateityp: string }, value: string): void {
    param.modus = value;
    if (value === 'Prüfparameter' || value === 'Eigener Wert') {
      param.dateityp = 'Text';
    }
    this.outputParameters = [...this.outputParameters];
  }

  isLockedOutputParameter(param: { modus: string; name: string; wert: string; dateityp: string }): boolean {
    return param.modus === 'KI-Generiert' && (param.name === 'Ergebnis' || param.name === 'Antwort');
  }

  isDateitypDisabled(param: { modus: string; name: string; wert: string; dateityp: string }): boolean {
    return this.isLockedOutputParameter(param) || param.modus === 'Prüfparameter' || param.modus === 'Eigener Wert';
  }

  onSystempromptChange(event: Event): void {
    this.systemprompt = (event.target as HTMLTextAreaElement).value;
    this.updatePromptCursorState('system', event);
  }

  onPruefpromptChange(event: Event): void {
    this.pruefprompt = (event.target as HTMLTextAreaElement).value;
    this.updatePromptCursorState('pruef', event);
  }

  onSystempromptCursorChange(event: Event): void {
    this.updatePromptCursorState('system', event);
  }

  onPruefpromptCursorChange(event: Event): void {
    this.updatePromptCursorState('pruef', event);
  }

  /** Hardcoded Ausgangspunkt für "Vorschlag anzeigen" – dient als Vorlage, kein KI-Aufruf. */
  protected readonly suggestedSystemprompt =
    'Du bist ein KI-Prüf-Agent im Bauwesen. Deine Aufgabe ist es, das vorgelegte Dokument oder Modell '
    + 'ausschließlich anhand der im Prüfprompt genannten Kriterien zu bewerten – sachlich, nachvollziehbar '
    + 'und ausschließlich auf Basis der bereitgestellten Inhalte. Triff keine Annahmen über Informationen, '
    + 'die nicht im Dokument enthalten sind, und kennzeichne Unklarheiten explizit statt sie zu erraten. '
    + 'Antworte immer in der von {Prüfregelname} vorgegebenen Struktur und verwende ausschließlich die im '
    + 'Ausgabeformat definierten Felder.';

  /** Hardcoded Ausgangspunkt für "Vorschlag anzeigen" – dient als Vorlage, kein KI-Aufruf. */
  protected readonly suggestedPruefprompt =
    'Prüfe anhand von {Prüfregelname}, ob das vorliegende Dokument die folgenden Kriterien erfüllt:\n'
    + '1. [Kriterium 1 – konkret und prüfbar formulieren]\n'
    + '2. [Kriterium 2 – konkret und prüfbar formulieren]\n'
    + '3. [Kriterium 3 – konkret und prüfbar formulieren]\n\n'
    + 'Gib für jedes Kriterium an, ob es erfüllt, teilweise erfüllt oder nicht erfüllt ist, und begründe '
    + 'die Einschätzung mit einem konkreten Verweis auf die entsprechende Stelle im Dokument.';

  private readonly systempromptOptimizationContext =
    'Es handelt sich um den Systemprompt eines KI-Prüf-Agenten: Er legt Rolle, Verhalten und '
    + 'Rahmenbedingungen des Agenten fest, der Dokumente oder Modelle automatisiert prüft.';
  private readonly systempromptOptimizationSystemPrompt =
    'Du bist ein Assistent, der Systemprompts für KI-Prüf-Agenten klarer, präziser und vollständiger '
    + 'formuliert, und dabei Rolle, Verhalten und Rahmenbedingungen erhält. Gib nur den optimierten '
    + 'Systemprompt als reinen Text zurück. Wiederhole niemals den Ausgangstext und verwende keinerlei Markdown.';
  private readonly pruefpromptOptimizationContext =
    'Es handelt sich um den Prüfprompt einer Prüfregel: Er ergänzt den Systemprompt um die konkreten '
    + 'Prüfschritte, nach denen ein Dokument oder Modell bewertet wird.';
  private readonly pruefpromptOptimizationSystemPrompt =
    'Du bist ein Assistent, der Prüfprompts klarer, präziser und vollständiger formuliert und dabei die '
    + 'konkreten Prüfschritte erhält. Gib nur den optimierten Prüfprompt als reinen Text zurück. '
    + 'Wiederhole niemals den Ausgangstext und verwende keinerlei Markdown.';

  optimizeSystemprompt(): void {
    const prompt = this.systemprompt.trim();
    if (!prompt || this.optimizingSystemprompt) return;

    this.optimizingSystemprompt = true;
    this.workflowService.optimizePrompt(prompt, {
      context: this.systempromptOptimizationContext,
      systemPrompt: this.systempromptOptimizationSystemPrompt,
    }).subscribe({
      next: (optimized) => {
        this.previousSystemprompt = this.systemprompt;
        this.systemprompt = optimized;
        this.optimizingSystemprompt = false;
      },
      error: () => {
        this.optimizingSystemprompt = false;
        this.toastService.error('Systemprompt konnte nicht verbessert werden');
      },
    });
  }

  optimizePruefprompt(): void {
    const prompt = this.pruefprompt.trim();
    if (!prompt || this.optimizingPruefprompt) return;

    this.optimizingPruefprompt = true;
    this.workflowService.optimizePrompt(prompt, {
      context: this.pruefpromptOptimizationContext,
      systemPrompt: this.pruefpromptOptimizationSystemPrompt,
    }).subscribe({
      next: (optimized) => {
        this.previousPruefprompt = this.pruefprompt;
        this.pruefprompt = optimized;
        this.optimizingPruefprompt = false;
      },
      error: () => {
        this.optimizingPruefprompt = false;
        this.toastService.error('Prüfprompt konnte nicht verbessert werden');
      },
    });
  }

  restoreSystemprompt(): void {
    if (this.previousSystemprompt == null) return;
    this.systemprompt = this.previousSystemprompt;
    this.previousSystemprompt = null;
  }

  restorePruefprompt(): void {
    if (this.previousPruefprompt == null) return;
    this.pruefprompt = this.previousPruefprompt;
    this.previousPruefprompt = null;
  }

  openPromptSuggestionPopup(): void {
    this.promptSuggestionPopupVisible = true;
  }

  closePromptSuggestionPopup(): void {
    this.promptSuggestionPopupVisible = false;
  }

  /**
   * Übernimmt den hardcodierten Vorschlag. Bewusst UNABHÄNGIG von {@link previousSystemprompt}:
   * das Feld steuert, ob "Mit chatBIC verbessern" oder "Wiederherstellen" angezeigt wird – würde
   * die Übernahme es mitsetzen, verschwände der chatBIC-Button direkt nach der Übernahme.
   */
  applySuggestedSystemprompt(): void {
    this.systemprompt = this.suggestedSystemprompt;
  }

  /** Übernimmt den hardcodierten Vorschlag – unabhängig von {@link previousPruefprompt}, s. {@link applySuggestedSystemprompt}. */
  applySuggestedPruefprompt(): void {
    this.pruefprompt = this.suggestedPruefprompt;
  }

  appendPromptVariable(target: 'system' | 'pruef', variable: string): void {
    const currentValue = target === 'system' ? this.systemprompt : this.pruefprompt;
    const start = target === 'system'
      ? (this.systemPromptCursorStart ?? currentValue.length)
      : (this.pruefPromptCursorStart ?? currentValue.length);
    const end = target === 'system'
      ? (this.systemPromptCursorEnd ?? currentValue.length)
      : (this.pruefPromptCursorEnd ?? currentValue.length);
    const nextValue = `${currentValue.slice(0, start)}${variable}${currentValue.slice(end)}`;

    if (target === 'system') {
      this.systemprompt = nextValue;
      this.systemPromptCursorStart = start + variable.length;
      this.systemPromptCursorEnd = start + variable.length;
    } else {
      this.pruefprompt = nextValue;
      this.pruefPromptCursorStart = start + variable.length;
      this.pruefPromptCursorEnd = start + variable.length;
    }

    const textarea = target === 'system' ? this.systemPromptElement : this.pruefPromptElement;
    if (textarea) {
      const nextCursorPosition = target === 'system'
        ? this.systemPromptCursorStart!
        : this.pruefPromptCursorStart!;
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(nextCursorPosition, nextCursorPosition);
      });
    }
  }

  private updatePromptCursorState(target: 'system' | 'pruef', event: Event): void {
    const textarea = event.target as HTMLTextAreaElement | null;
    if (!textarea || typeof textarea.selectionStart !== 'number' || typeof textarea.selectionEnd !== 'number') {
      return;
    }

    if (target === 'system') {
      this.systemPromptElement = textarea;
      this.systemPromptCursorStart = textarea.selectionStart;
      this.systemPromptCursorEnd = textarea.selectionEnd;
    } else {
      this.pruefPromptElement = textarea;
      this.pruefPromptCursorStart = textarea.selectionStart;
      this.pruefPromptCursorEnd = textarea.selectionEnd;
    }
  }

  private loadDocumentClasses(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) {
      this.availableDocumentClasses = [];
      return;
    }

    this.documentClassService.list(projectId).subscribe({
      next: response => {
        this.availableDocumentClasses = (response.classes ?? [])
          .map(documentClass => ({id: documentClass.id, name: documentClass.title}));
        // Die Klassen treffen unabhängig vom Regelset ein: kommen sie später, steht im Formular
        // noch die leere Auswahl aus restoreSetConfig – sie wird hier nachgezogen. Der Nutzer hat
        // dabei nichts geändert, deshalb darf der Editor davon nicht „ungespeichert" werden.
        const wasPristine = !this.isDirty;
        if (this.backfillAllowedDocumentClassesIfEmpty() && wasPristine) {
          this.markSaved();
        }
      },
      error: () => {
        this.availableDocumentClasses = [];
      },
    });
  }

  /**
   * Solange keine Einschränkung getroffen wurde, gelten alle Dokumentenklassen als erlaubt.
   *
   * @returns true, wenn die Auswahl dadurch gesetzt wurde
   */
  private backfillAllowedDocumentClassesIfEmpty(): boolean {
    if (this.getAllowedDocumentClassesValue().length > 0) return false;
    const allDocumentClassIds = this.availableDocumentClasses.map(documentClass => documentClass.id);
    if (allDocumentClassIds.length === 0) return false;
    this.form.get('allowedDocumentClasses')?.setValue(allDocumentClassIds);
    return true;
  }

  private parseStoredDocumentClasses(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.filter((v): v is string => typeof v === 'string');
  }

  private parseStoredAllowedFileTypes(value: unknown): string[] {
    const normalizedValues = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? value.split(',').map(v => v.trim()).filter(Boolean)
        : [];

    return normalizedValues
      .map(v => String(v).toUpperCase())
      .map(v => {
        if (v === '.IFC' || v === 'IFC') return 'IFC';
        if (v === '.PDF' || v === 'PDF') return 'PDF';
        if (v === '.XLSX' || v === '.XLS' || v === 'EXCEL' || v === 'XLSX') return 'Excel';
        if (v === '.DOCX' || v === '.DOC' || v === 'WORD') return 'Word';
        if (v === '.PPTX' || v === '.PPT' || v === 'POWERPOINT') return 'PowerPoint';
        return '';
      })
      .filter(Boolean);
  }

  private serializeAllowedFileTypes(value: unknown): string[] {
    const selected = Array.isArray(value)
      ? value.filter((v): v is string => typeof v === 'string')
      : [];

    return selected
      .map(v => {
        if (v === 'IFC') return '.ifc';
        if (v === 'PDF') return '.pdf';
        if (v === 'Excel') return '.xlsx';
        if (v === 'Word') return '.docx';
        if (v === 'PowerPoint') return '.pptx';
        return '';
      })
      .filter(Boolean);
  }

  private restoreSetConfig(config: any): void {
    const allowedFileTypes = this.parseStoredAllowedFileTypes(config.allowedFileTypes);
    const parsedAllowedDocumentClasses = this.parseStoredDocumentClasses(config.allowedDocumentClasses);
    const allowedDocumentClasses = parsedAllowedDocumentClasses.length
      ? parsedAllowedDocumentClasses
      : this.availableDocumentClasses.map(documentClass => documentClass.id);
    this.form.patchValue({
      allowedFileTypes: allowedFileTypes.length ? allowedFileTypes : this.ruleset.allowedFileTypes,
      allowedDocumentClasses,
      checkMode: config.checkMode ?? this.ruleset.checkMode,
    }, {emitEvent: false});

    this.inputParameters = config.inputParameters?.length ? config.inputParameters : [
      {id: crypto.randomUUID(), name: 'Dateitext', dataType: 'Text', necessity: 'Pflicht'},
      {id: crypto.randomUUID(), name: 'Dateiname', dataType: 'Text', necessity: 'Pflicht'},
    ];
    this.checkParameters = config.checkParameters?.length ? config.checkParameters : [
      {id: crypto.randomUUID(), name: 'Neuer Prüfparameter', dataType: 'Text', necessity: 'Optional'},
    ];
    this.tags = config.tags?.length ? config.tags : [];
    this.eingabeparameter = this.restoreEingabeparameter(config);
    this.pruefebene = normalizePruefebene(config.pruefebene);
    const storedIdentifikator = typeof config.identifikatorParamId === 'string' ? config.identifikatorParamId : '';
    this.identifikatorParamId = this.eingabeparameter.some(param => param.id === storedIdentifikator)
      ? storedIdentifikator
      : (this.eingabeparameter[0]?.id ?? '');
    this.pruefebeneFilter = this.pruefebene !== 'gesamtdokument' && typeof config.pruefebeneFilter === 'string'
      ? config.pruefebeneFilter
      : '';
    if (config.kiModel) this.aiModel = config.kiModel;
    if (config.systemprompt) this.systemprompt = config.systemprompt;
    if (config.pruefprompt) this.pruefprompt = config.pruefprompt;
    if (config.contextFiles?.length) this.contextFiles = config.contextFiles;
    const savedOutputParams = config.outputParameters?.length ? config.outputParameters : null;
    // Migration: remove deprecated output entries if present in saved data
    const filteredOutputParams = savedOutputParams
      ? savedOutputParams.filter((p: any) =>
        !(p.modus === 'KI-Generiert' && (p.name === 'Regeltyp' || p.name === 'Bewertung')))
      : null;
    this.outputParameters = filteredOutputParams?.length ? filteredOutputParams : [
      {modus: 'KI-Generiert', name: 'Ergebnis', wert: 'Gesamtergebnis der Prüfung entweder SUCCESS, FAILURE, WARNING oder UNKNOWN (falls die Datei für die Regel nicht relevant ist oder keine ausreichende Information für ein Ergebnis vorliegt)', dateityp: 'Text'},
      {modus: 'KI-Generiert', name: 'Antwort', wert: 'Kurze sachliche Antwort, warum die Regel erfüllt wurde oder nicht.', dateityp: 'Text'},
    ];

    this.outputParameters = this.outputParameters.map((param) =>
      (param.modus === 'Prüfparameter' || param.modus === 'Eigener Wert')
        ? {...param, dateityp: 'Text'}
        : param,
    );
    // Altformat überführen: Auswahl-Parameter trugen ihre Optionen kommasepariert im `wert`-Feld.
    this.outputParameters = migrateOutputParameterOptions(this.outputParameters);
  }
}
